/**
 * AdminPage - Full CRUD admin dashboard for managing platform data.
 *
 * Provides tabbed navigation between three management sections:
 *	 1. Users	  — list, search, filter, add, edit, deactivate/reactivate users
 *	 2. Companies — list, search, add, edit, deactivate/reactivate companies
 *	 3. Buildings  — list, search, filter by business_line, add, edit, deactivate/reactivate buildings (location records)
 *
 * Each tab fetches data from its respective API endpoint, supports keyword search,
 * and displays results in a responsive table (desktop) or card layout (mobile).
 * CRUD operations are performed via shared AdminModal (Modal organism) dialogs
 * with form validation, loading states, and success/error feedback.
 *
 * Access control: only users with role 'admin' or 'developer' should see
 * the Admin link in the nav. The route guard in App.tsx handles enforcement.
 *
 * Accessible by: admin, developer roles (protected by <ProtectedRoute>).
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Modal } from '../../components/organisms/Modal';
import { ConfirmModal } from '../../components/organisms/ConfirmModal';
import { CsvImport } from '../../components/organisms/CsvImport';
import { ResponsiveFilters } from '../../components/organisms/ResponsiveFilters';
import type { ImportResult } from '../../services/importService';
import type { ImportEntityType } from '../../components/organisms/CsvImport';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/molecules/PageHeader';
import { TabBar } from '../../components/molecules/TabBar';
import { Alert, Button, Checkbox } from '../../components/atoms';
import { SearchBar } from '../../components/molecules/SearchBar';
import { AddressInput } from '../../components/molecules/AddressInput';
import { SearchableSelect } from '../../components/molecules/SearchableSelect';
import { SearchableMultiSelect } from '../../components/molecules/SearchableMultiSelect';
import { BuildingFieldset } from '../../components/molecules/BuildingFieldset';
import { HubSpotConnection } from '../../components/organisms/HubSpotConnection';
import { HubSpotSync } from '../../components/organisms/HubSpotSync';
import BillingTab from './BillingTab';
import * as hubspotService from '../../services/hubspotService';
import type { HubSpotConnectionStatus } from '../../services/hubspotService';
import { dedupeAndSort, dedupeAndSortStrings } from '../../utils/dedupeAndSort';
import { formatPhone } from '../../utils/formatPhone';
import { businessLineService, BusinessLine } from '../../services/businessLineService';
import { formatRoleLabel } from '../../utils/formatLabel';
import { rolesAllowMissingCredentials, primaryRoleLabel, DEFAULT_ASSIGNED_ROLES, DEFAULT_USER_ROLE_LABEL } from '../../utils/constants';
import { RoleSelector } from '../../components/molecules/RoleSelector';
import { FORM_INPUT_CLASS, FORM_SELECT_CLASS, FORM_LABEL_CLASS } from '../../utils/formClasses';
import { qk } from '../../lib/queryKeys';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Available admin tabs — includes the Jobs tab for combined WO + Inspection view
 *  and the Integrations tab for HubSpot sync (admin/developer only) */
type AdminTab = 'users' | 'companies' | 'contacts' | 'locations' | 'jobs' | 'billing' | 'integrations';

/** Human-readable singular noun for each admin tab, used in confirmation dialogs */
const TAB_NOUN: Record<AdminTab, string> = {
	users: 'user',
	companies: 'company',
	contacts: 'contact',
	locations: 'building',
	jobs: 'job',
	billing: 'work order',
	integrations: 'integration',
};

/** Job type filter options for the Jobs tab */
type JobTypeFilter = 'all' | 'work_order' | 'inspection';

/** Combined job record shape — unifies Work Orders and Inspections into one table row */
interface JobRecord {
	/** Original record ID from the API */
	id: string | number;
	/** Discriminator tag: 'Work Order' or 'Inspection' */
	jobType: 'Work Order' | 'Inspection';
	/** Subject line (WO) or building name (Inspection) */
	subject: string;
	/** Company name associated with the job */
	companyName: string;
	/** Assignee's full name */
	assigneeName: string;
	/** True when `date` is a calendar day (an inspection's DATE column), which
	 *  must not be read as an instant — that lands on the day before anywhere
	 *  west of Greenwich. See formatJobDate. */
	dateIsDay?: boolean;
	/** Current status (e.g., open, in_progress, completed, pending, passed, failed) */
	status: string;
	/** ISO date string for display (created_at or scheduled_date) */
	date: string;
}

/** A Jobs-tab date in the column's numeric format. A calendar day is parsed
 *  at local midnight (as formatDay does) so it keeps the day that was picked;
 *  a timestamp is shown as the local date it falls on. */
const formatJobDate = (job: JobRecord): string => {
	if (!job.date) return '--';
	const d = job.dateIsDay ? new Date(`${String(job.date).slice(0, 10)}T00:00:00`) : new Date(job.date);
	return d.toLocaleDateString();
};

/** User record shape from the API (snake_case from Laravel) */
interface ApiUser {
	id: string | number;
	first_name: string;
	last_name: string;
	/** A subcontractor's own business name, e.g. "Cyclone Window Cleaning". */
	subcontractor_company?: string | null;
	/** Null for CSR / JSR / Other users created from a name alone — they have
	 *  no login identifier and therefore cannot sign in. */
	email: string | null;
	phone?: string;
	role: string;
	/** Array of role slugs from the user_roles junction table (appended by User model) */
	role_names?: string[];
	company_id: string | number | null;
	/** Array of all company IDs from the company_users junction table (appended by User model) */
	company_ids?: (string | number)[];
	company_name?: string;
	/** The user's companies by name; pivot.is_primary marks the primary one. */
	companies?: { company_id: string | number; name: string; pivot?: { is_primary?: boolean | number } }[];
	status: string;
	can_view_financials?: boolean;
	can_reassign?: boolean;
}

/** Company record shape from the API.
 *	Physical addresses are stored in the locations table (linked via
 *	the company_locations pivot). Only billing address fields remain
 *	directly on the company. */
interface ApiCompany {
	id: string | number;
	name: string;
	phone?: string;
	email: string;
	billing_email?: string;
	billing_phone?: string;
	tax_rate?: number;
	billing_terms?: string;
	notes?: string;
	status?: string;
	is_active?: boolean;
	/** Billing address fields — where invoices are sent */
	billing_address?: string;
	billing_address_line_2?: string;
	billing_city?: string;
	billing_state?: string;
	billing_zip?: string;
}

/** Contact record shape from the API (snake_case from Laravel).
 *	Contacts are customer-side people (site managers, superintendents) —
 *	NOT platform users. Each belongs to exactly one company; the counts
 *	arrive when the list is fetched with with_counts=1 and quantify how
 *	many records reference the contact (context for reassignment). */
interface ApiContact {
	id: string | number;
	first_name: string;
	last_name: string;
	full_name?: string;
	phone?: string | null;
	email?: string | null;
	title?: string | null;
	notes?: string | null;
	company_id: string | number;
	/** Eager-loaded parent company (the customer). */
	company?: { company_id: string | number; name: string } | null;
	status?: string;
	work_orders_count?: number;
	inspections_count?: number;
}

/** Location record shape from the API.
 *	Address fields are nullable — only the name is required at creation
 *	(the locations table allows null address/city/state/zip). */
interface ApiLocation {
	id: string | number;
	name: string;
	address: string | null;
	/** Address line 2 (suite, unit, dock, gate) for this location */
	address_line_2?: string | null;
	city: string | null;
	state: string | null;
	zip?: string | null;
	cleanable_sqft?: number;
	tax_rate?: number;
	business_line?: string;
	notes?: string;
	status?: string;
	is_active?: boolean;
	/** Associated companies (from the company_locations pivot) */
	companies?: { company_id: number; name: string }[];
}

/* ============================================
 * FORM INTERFACES
 * ============================================ */

/** Fields for the User create/edit modal */
interface UserFormData {
	first_name: string;
	last_name: string;
	/** Subcontractor's business name — only shown/sent while the
	 *  Subcontractor role is ticked. */
	subcontractor_company: string;
	email: string;
	phone: string;
	role: string;
	/** Array of selected role slugs for multi-role assignment via the user_roles junction table */
	roles: string[];
	company_id: string;
	/** Array of additional company IDs (beyond the primary) to associate the user with
	 *	via the company_users junction table. Does not include the primary company_id. */
	additional_company_ids: string[];
	can_view_financials: boolean;
	can_reassign: boolean;
}

/** Fields for the Company create/edit modal.
 *	Physical address is managed via the Buildings tab.
 *	Only billing address fields remain on the company form. */
interface CompanyFormData {
	name: string;
	phone: string;
	billing_email: string;
	/** Accounts-payable phone — inherited by the work order billing block */
	billing_phone: string;
	tax_rate: string;
	billing_terms: string;
	notes: string;
	/** Billing address fields — where invoices are sent */
	billing_address: string;
	billing_address_line_2: string;
	billing_city: string;
	billing_state: string;
	billing_zip: string;
}

/** Fields for the Contact create/edit modal.
 *	company_id is the customer the contact belongs to — changing it on an
 *	existing contact reassigns the contact to a different customer. */
interface ContactFormData {
	first_name: string;
	last_name: string;
	company_id: string;
	phone: string;
	email: string;
	title: string;
	notes: string;
}

/** Fields for the Location create/edit modal */
interface LocationFormData {
	name: string;
	address: string;
	/** Address line 2 for suite/unit/dock/gate at this location */
	address_line_2: string;
	city: string;
	state: string;
	zip: string;
	cleanable_sqft: string;
	tax_rate: string;
	business_line: string;
	notes: string;
	company_ids: string[];
}

/* ============================================
 * CONSTANTS
 * ============================================ */

/** Tab metadata for rendering the tab bar — Jobs tab is visible to admin/super_admin/developer.
 *  Integrations tab is added dynamically below (only for admin/developer roles). */
const TABS: { key: AdminTab; label: string }[] = [
	{ key: 'users', label: 'Users' },
	{ key: 'companies', label: 'Companies' },
	{ key: 'contacts', label: 'Contacts' },
	{ key: 'locations', label: 'Buildings' },
	{ key: 'jobs', label: 'Jobs' },
	{ key: 'billing', label: 'Billing' },
	{ key: 'integrations', label: 'Integrations' },
];

/** Tabs backed by a CSV import endpoint, in tab order. Doubles as the guard
 *  for the "Import CSV" button: the toolbar also renders on Integrations,
 *  which is not an entity tab at all, so the button keys off this list rather
 *  than off "not one of the tabs we happen to exclude". The ImportEntityType
 *  annotation makes a tab added here without a matching import endpoint a
 *  compile error rather than a 404 at upload time. */
const IMPORTABLE_TABS: ImportEntityType[] = ['users', 'companies', 'contacts', 'locations'];

/** Id linking the CRUD modal's <form> to the Save button pinned in the modal
 *  footer (the button lives outside the form element, so it needs form="…"). */
const ADMIN_MODAL_FORM_ID = 'admin-crud-form';

/** Role options for the user form and filter dropdowns — includes super_admin for completeness.
 *  'subcontractor' is included so admins can filter the user list to external vendors and so
 *  the role-edit dropdown can promote/demote a user into the subcontractor role. 'csr' / 'jsr' /
 *  'other' are the service-rep roles, listed here for the same reason — an admin needs to be
 *  able to filter the list down to them. */
const ROLE_OPTIONS = ['super_admin', 'admin', 'manager', 'technician', 'reviewer', 'developer', 'company_admin', 'subcontractor', 'csr', 'jsr', 'other'];

/** Status filter options for the users tab */
const STATUS_OPTIONS = ['active', 'inactive', 'locked'];

/* Business Line options for locations are now fetched from the
   CMS-managed business_lines table — see the businessLines effect
   inside the component. The hardcoded ['janitorial','construction','both']
   array was retired alongside the WO/Inspection form refactor on
   2026-05-24. */


/** Billing terms options for companies */
const BILLING_TERMS_OPTIONS = ['Net 15', 'Net 30', 'Net 45', 'Net 60'];

/** Default empty state for the user form.
 *	The roles array defaults to ['reviewer'] — this is the safest default
 *	role with the least permissions for newly created users. */
const EMPTY_USER_FORM: UserFormData = {
	first_name: '',
	last_name: '',
	subcontractor_company: '',
	email: '',
	phone: '',
	role: DEFAULT_USER_ROLE_LABEL,
	roles: [...DEFAULT_ASSIGNED_ROLES],
	company_id: '',
	additional_company_ids: [],
	can_view_financials: false,
	can_reassign: false,
};

/** Default empty state for the company form.
 *	Physical address is managed via the Buildings tab — only billing fields here. */
const EMPTY_COMPANY_FORM: CompanyFormData = {
	name: '',
	phone: '',
	billing_email: '',
	billing_phone: '',
	tax_rate: '',
	billing_terms: 'Net 30',
	notes: '',
	/* Billing address fields */
	billing_address: '',
	billing_address_line_2: '',
	billing_city: '',
	billing_state: '',
	billing_zip: '',
};

/** Default empty state for the contact form */
const EMPTY_CONTACT_FORM: ContactFormData = {
	first_name: '',
	last_name: '',
	company_id: '',
	phone: '',
	email: '',
	title: '',
	notes: '',
};

/** Default empty state for the location form */
const EMPTY_LOCATION_FORM: LocationFormData = {
	name: '',
	address: '',
	address_line_2: '',
	city: '',
	state: '',
	zip: '',
	cleanable_sqft: '',
	tax_rate: '',
	business_line: '',
	notes: '',
	company_ids: [],
};

/* ============================================
 * HELPERS
 * ============================================ */


/**
 * Convert a stored decimal tax rate (the API serializes the decimal(5,4)
 * column as a string, e.g. "0.0700") to the percent string the
 * "Tax Rate (%)" inputs expect (e.g. "7"). Inverse of the /100 applied on
 * save. Rounding via integer math is exact for 4-decimal stored rates and
 * avoids float artifacts like 0.07 * 100 === 7.000000000000001.
 */
const taxRateToPercent = (rate: number | string): string =>
	String(Math.round(Number(rate) * 10000) / 100);

/**
 * Returns Tailwind classes for a status badge based on active/inactive/locked state.
 * Active states get green, inactive get gray, locked get red.
 */
const getStatusBadgeClass = (status: string): string => {
	const normalized = status?.toLowerCase() || 'inactive';
	if (normalized === 'active' || normalized === 'true') return 'bg-green-100 text-green-800';
	if (normalized === 'locked') return 'bg-red-100 text-red-800';
	return 'bg-gray-100 text-gray-600';
};

/**
 * Simple email validation using a regex pattern.
 * Returns true if the email format appears valid.
 */
const isValidEmail = (email: string): boolean => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Shared styling for the toolbar's per-tab filter dropdowns.
 *
 * Five of them — roles, statuses, customers, contact statuses, business lines
 * — each rendered only on the tab it belongs to, so they can't be folded into
 * one FilterBar the way the billing queue's can. Sharing the class string is
 * what keeps the strip reading as one row of controls rather than five
 * separately-tuned ones. min-h-44px holds the touch floor (CONVENTIONS.md §5);
 * the padding alone lands at ~37px.
 */
const FILTER_SELECT_CLASS = 'admin-page__filter px-12px py-8px min-h-44px desktop:min-h-36px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary';

/**
 * UserCompanies - a user's companies by name, for the Users list.
 *
 * One company shows just its name. Several show a "Primary:" line and an
 * "Additional:" line (alphabetical), the same words the user form uses.
 * The column used to print users.company_id, so it read "2" or "3".
 * `labelled` is for the phone card, which has no column header: a lone
 * company gets "Company:" in front of it there.
 */
export const UserCompanies: React.FC<{ user: Pick<ApiUser, 'companies' | 'company_id'>; labelled?: boolean }> = ({ user, labelled = false }) => {
	const companies = user.companies ?? [];
	const primary = companies.find((c) => Boolean(c.pivot?.is_primary))
		?? companies.find((c) => String(c.company_id) === String(user.company_id));
	const others = dedupeAndSortStrings(companies.filter((c) => c !== primary).map((c) => c.name));
	const label = (text: string) => <span className="text-brand-text-subtle">{text} </span>;

	if (companies.length === 0) return <span className="admin-page__user-companies">—</span>;
	if (!primary) return <span className="admin-page__user-companies">{labelled && label('Companies:')}{others.join(', ')}</span>;
	if (others.length === 0) return <span className="admin-page__user-companies">{labelled && label('Company:')}{primary.name}</span>;
	return (
		<span className="admin-page__user-companies block">
			<span className="block">{label('Primary:')}{primary.name}</span>
			<span className="block">{label('Additional:')}{others.join(', ')}</span>
		</span>
	);
};

/**
 * RowActions - Edit and Deactivate (or Reactivate) for one record on an Admin
 * list, plus whatever the tab adds as children (Users: Reset PW, Sign In As).
 *
 * These were coloured text links with nothing around them; three or four to
 * a row they read like a sentence, not like things to press. They are small
 * Buttons now: Edit and Reactivate in the brand outline, Deactivate in a red
 * one so the destructive action stands apart, extras in the neutral outline.
 *
 * `layout="card"` is the phone card: two columns of full-width buttons, so
 * four actions make a tidy 2×2 instead of a ragged wrap. Children get the
 * full width from the grid too, so callers pass plain Buttons either way.
 */
const RowActions: React.FC<{
	layout: 'row' | 'card';
	isInactive: boolean;
	onEdit: () => void;
	onDeactivate: () => void;
	onReactivate: () => void;
	children?: React.ReactNode;
}> = ({ layout, isInactive, onEdit, onDeactivate, onReactivate, children }) => (
	<div
		className={layout === 'row'
			? 'admin-page__row-actions flex flex-wrap items-center gap-8px [&>*]:whitespace-nowrap'
			: 'admin-page__card-actions grid grid-cols-2 gap-8px mt-12px pt-12px border-t border-brand-border/50 [&>*]:w-full'}
	>
		<Button variant="secondary" size="sm" onClick={onEdit}>Edit</Button>
		{isInactive
			? <Button variant="secondary" size="sm" onClick={onReactivate}>Reactivate</Button>
			: <Button variant="danger-outline" size="sm" onClick={onDeactivate}>Deactivate</Button>}
		{children}
	</div>
);

/* ============================================
 * COMPONENT
 * ============================================ */

const AdminPage: React.FC = () => {
	/* --- Auth context for role checks and impersonation --- */
	const { hasRole, hasAnyRole, impersonate } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	/* --- Whether the current user is a super_admin (can impersonate others) --- */
	const isSuperAdmin = hasRole('super_admin');

	/* --- Whether the current user is a "super" user (super_admin, admin, or developer).
		   This decides which tabs show (Companies, Contacts, Jobs), not which data: the
		   server limits an admin to the companies they belong to, and only super_admin
		   and developer see every company (App\Traits\CompanyScoped). company_admin is
		   limited to its own company and gets none of these tabs. --- */
	const isSuperUser = hasAnyRole(['super_admin', 'admin', 'developer']);

	/* --- Tab state --- */
	const [activeTab, setActiveTab] = useState<AdminTab>('users');

	/* --- Search and filter state --- */
	const [searchQuery, setSearchQuery] = useState('');
	const [roleFilter, setRoleFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [businessLineFilter, setBusinessLineFilter] = useState('');
	/** Customer filter for the Contacts tab — '' shows every customer's contacts */
	const [contactCompanyFilter, setContactCompanyFilter] = useState('');

	/* --- Pagination state for the users tab.
		   Backend returns Laravel pagination meta (current_page, last_page, total)
		   alongside the data array. The current page is page-local UI state; the
		   last_page/total bounds are derived from the query result below. --- */
	const [usersPage, setUsersPage] = useState(1);

	/** Type filter for the Jobs tab: all, work_order, or inspection */
	const [jobTypeFilter, setJobTypeFilter] = useState<JobTypeFilter>('all');

	/* --- Mutation/action error surfaced inline (deactivate/reactivate/impersonate).
		   Kept separate from the list query error so a failed action doesn't blank
		   the table. --- */
	const [actionError, setActionError] = useState<string | null>(null);

	/* --- Success feedback message (auto-dismisses) --- */
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	/* --- Modal state --- */
	const [modalOpen, setModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
	const [modalSaving, setModalSaving] = useState(false);
	const [modalError, setModalError] = useState<string | null>(null);
	const [editingId, setEditingId] = useState<string | number | null>(null);

	/* --- Form validation errors --- */
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});

	/* --- Form state for each tab --- */
	const [userForm, setUserForm] = useState<UserFormData>(EMPTY_USER_FORM);
	const [companyForm, setCompanyForm] = useState<CompanyFormData>(EMPTY_COMPANY_FORM);
	const [contactForm, setContactForm] = useState<ContactFormData>(EMPTY_CONTACT_FORM);
	const [locationForm, setLocationForm] = useState<LocationFormData>(EMPTY_LOCATION_FORM);
	/** Reference counts of the contact being edited — shown next to the
	 *	customer picker so reassignment is an informed action. */
	const [editingContactCounts, setEditingContactCounts] = useState<{ workOrders: number; inspections: number } | null>(null);
	/** The customer the contact belonged to when the edit modal opened —
	 *	used to detect and call out a pending reassignment. */
	const [editingContactOriginalCompanyId, setEditingContactOriginalCompanyId] = useState('');

	/* --- CSV Import modal state --- */
	/** Whether the CSV import modal is currently open */
	const [csvImportOpen, setCsvImportOpen] = useState(false);
	/** Which entity type the CSV import modal is currently targeting */
	const [csvImportEntity, setCsvImportEntity] = useState<ImportEntityType>('users');

	/* --- Duplicate detection state for manual create forms --- */
	/** Warning message when a duplicate user email is detected */
	const [userDuplicateWarning, setUserDuplicateWarning] = useState<string | null>(null);
	/** Warning message when a duplicate company name is detected */
	const [companyDuplicateWarning, setCompanyDuplicateWarning] = useState<string | null>(null);
	/** Warning message when a duplicate location name+address is detected */
	const [locationDuplicateWarning, setLocationDuplicateWarning] = useState<string | null>(null);

	/** Ref for the debounce timer used in duplicate detection — prevents
	 *	excessive API calls by waiting 300ms after the last keystroke */
	const duplicateCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	/* --- Reset Password modal state --- */
	/** Whether the reset password modal is currently open */
	const [resetPwModalOpen, setResetPwModalOpen] = useState(false);
	/** The user whose password is being reset */
	const [resetPwTarget, setResetPwTarget] = useState<ApiUser | null>(null);
	/** The temporary password entered in the modal */
	const [resetPwValue, setResetPwValue] = useState('');
	/** Whether the reset password request is in progress */
	const [resetPwLoading, setResetPwLoading] = useState(false);
	/** Inline validation error for the password field */
	const [resetPwError, setResetPwError] = useState<string | null>(null);

	/* --- Deactivate confirmation modal state --- */
	/** The record pending deactivation, or null when the modal is closed */
	const [deactivateTarget, setDeactivateTarget] = useState<{ tab: AdminTab; id: string | number } | null>(null);

	/* ============================================
	 * DATA FETCHING (TanStack Query)
	 * ============================================ */

	/* Reset users-tab pagination to page 1 whenever the filters or active tab change.
	   Without this, switching from a multi-page filtered view back to an unfiltered
	   view leaves usersPage on a page that may no longer exist. */
	useEffect(() => {
		setUsersPage(1);
	}, [activeTab, searchQuery, roleFilter, statusFilter]);

	/* Trimmed search string shared by all the tab queries below. */
	const trimmedSearch = searchQuery.trim();

	/* --- Users list query (paginated). Keyed by search + filters + page so
		   changing any of them refetches and shares the cache. The queryFn
		   returns the rows AND the pagination meta in one object; the
		   component reads each off the result. --- */
	const usersQuery = useQuery({
		queryKey: qk.users.list({ search: trimmedSearch, role: roleFilter, status: statusFilter, page: usersPage }),
		enabled: activeTab === 'users',
		queryFn: async () => {
			const params: Record<string, string> = {};
			if (trimmedSearch) params.search = trimmedSearch;
			if (roleFilter) params.role = roleFilter;
			if (statusFilter) params.status = statusFilter;
			params.page = String(usersPage);
			const res = await api.get<any>('/users', params);
			const userData = (Array.isArray(res.data) ? res.data : (res.data?.data ?? [])) as ApiUser[];
			const meta = res.data && !Array.isArray(res.data)
				? { lastPage: res.data.last_page ?? 1, total: res.data.total ?? userData.length }
				: { lastPage: 1, total: userData.length };
			return { users: userData, ...meta };
		},
	});

	/* --- Companies list query --- */
	const companiesQuery = useQuery({
		queryKey: qk.companies.list({ search: trimmedSearch }),
		enabled: activeTab === 'companies',
		queryFn: async () => {
			const params: Record<string, string> = {};
			if (trimmedSearch) params.search = trimmedSearch;
			const res = await api.get<any>('/companies', params);
			const companyData = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
			/* The Company model uses 'company_id' as the primary key, so the
			   raw API rows don't have a top-level `id`. Mirror it here so the
			   table renderers (which read `c.id` for keys, edit, deactivate)
			   work without each callsite having to know about the custom PK. */
			return companyData.map((c: any) => ({ ...c, id: c.id ?? c.company_id })) as ApiCompany[];
		},
	});

	/* --- Contacts list query (customer contact database) ---
		   status defaults to 'all' so deactivated contacts stay visible for
		   reactivation (the backend's plain default is active-only, which is
		   right for pickers but wrong for an admin surface). with_counts adds
		   work_orders_count / inspections_count per row so the table and the
		   reassignment modal can show what references each contact. --- */
	const contactsQuery = useQuery({
		queryKey: qk.contacts.list({ search: trimmedSearch, status: statusFilter || 'all', company_id: contactCompanyFilter }),
		enabled: activeTab === 'contacts',
		queryFn: async () => {
			const params: Record<string, string> = {
				status: statusFilter || 'all',
				with_counts: '1',
				per_page: '999',
			};
			if (trimmedSearch) params.search = trimmedSearch;
			if (contactCompanyFilter) params.company_id = contactCompanyFilter;
			const res = await api.get<any>('/contacts', params);
			return (Array.isArray(res.data) ? res.data : (res.data?.data ?? [])) as ApiContact[];
		},
	});

	/* --- Locations list query --- */
	const locationsQuery = useQuery({
		queryKey: qk.locations.list({ search: trimmedSearch, business_line: businessLineFilter }),
		enabled: activeTab === 'locations',
		queryFn: async () => {
			const params: Record<string, string> = {};
			if (trimmedSearch) params.search = trimmedSearch;
			if (businessLineFilter) params.business_line = businessLineFilter;
			const res = await api.get<any>('/locations', params);
			return (Array.isArray(res.data) ? res.data : (res.data?.data ?? [])) as ApiLocation[];
		},
	});

	/* --- Jobs query: combined Work Orders + Inspections. Uses a dedicated
		   key namespace since it merges two endpoints into one unified list. --- */
	const jobsQuery = useQuery({
		queryKey: ['adminJobs', 'list', { search: trimmedSearch }] as const,
		enabled: activeTab === 'jobs',
		queryFn: async () => {
			const params: Record<string, string> = {};
			if (trimmedSearch) params.search = trimmedSearch;
			const [woRes, inspRes] = await Promise.all([
				api.get<any>('/work-orders', params),
				api.get<any>('/inspections', params),
			]);

			/* Parse work orders — handle paginated or flat responses */
			const woData = Array.isArray(woRes.data) ? woRes.data : (woRes.data?.data ?? []);
			const woJobs: JobRecord[] = woData.map((wo: any) => ({
				id: wo.id,
				jobType: 'Work Order' as const,
				subject: wo.subject || wo.title || `WO #${wo.id}`,
				companyName: wo.company?.name || wo.company_name || '--',
				assigneeName: wo.assignee ? `${wo.assignee.first_name} ${wo.assignee.last_name}` : (wo.assignee_name || '--'),
				status: wo.status || 'open',
				date: wo.created_at || wo.date || '',
			}));

			/* Parse inspections — handle paginated or flat responses */
			const inspData = Array.isArray(inspRes.data) ? inspRes.data : (inspRes.data?.data ?? []);
			const inspJobs: JobRecord[] = inspData.map((insp: any) => ({
				id: insp.id,
				jobType: 'Inspection' as const,
				subject: insp.building_name || insp.location?.name || insp.title || `Inspection #${insp.id}`,
				companyName: insp.company?.name || insp.company_name || '--',
				assigneeName: insp.assignee ? `${insp.assignee.first_name} ${insp.assignee.last_name}` : (insp.inspector ? `${insp.inspector.first_name} ${insp.inspector.last_name}` : (insp.assignee_name || '--')),
				status: insp.status || 'pending',
				/* Inspections have no scheduled_date — their day is inspection_date.
				   This used to fall through to created_at, so the column showed
				   when the inspection was entered, not when it happens. */
				date: insp.inspection_date || insp.created_at || '',
				dateIsDay: Boolean(insp.inspection_date),
			}));

			/* Combine and sort by date descending (newest first) */
			return [...woJobs, ...inspJobs].sort((a, b) => {
				return new Date(b.date).getTime() - new Date(a.date).getTime();
			});
		},
	});

	/* Derive the tab data + loading/error from the active tab's query.
	   Integrations tab manages its own data, so it stays out of `loading`. */
	const users: ApiUser[] = useMemo(() => usersQuery.data?.users ?? [], [usersQuery.data]);
	const usersLastPage = usersQuery.data?.lastPage ?? 1;
	const usersTotal = usersQuery.data?.total ?? 0;
	const companies: ApiCompany[] = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);
	const contacts: ApiContact[] = useMemo(() => contactsQuery.data ?? [], [contactsQuery.data]);
	const locations: ApiLocation[] = useMemo(() => locationsQuery.data ?? [], [locationsQuery.data]);
	const jobs: JobRecord[] = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);
	const jobsLoading = jobsQuery.isLoading;

	/* The active tab's load + error state, mirroring the old single `loading`/`error`. */
	const activeQuery = activeTab === 'users' ? usersQuery
		: activeTab === 'companies' ? companiesQuery
		: activeTab === 'contacts' ? contactsQuery
		: activeTab === 'locations' ? locationsQuery
		: activeTab === 'jobs' ? jobsQuery
		: null;
	const loading = activeQuery ? activeQuery.isLoading : false;
	const error = actionError ?? (activeQuery?.error ? (activeQuery.error as Error).message : null);

	/**
	 * Companies list for dropdown usage in user/location forms.
	 * per_page=999 avoids pagination truncating the dropdown.
	 */
	const companyOptionsQuery = useQuery({
		queryKey: qk.companies.list({ per_page: '999' }),
		queryFn: async () => {
			const res = await api.get<any>('/companies', { per_page: '999' });
			const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
			/* Company PK is 'company_id'; mirror to `id` so the dropdown
			   <option> values aren't the literal string "undefined". */
			return data.map((c: any) => ({ ...c, id: c.id ?? c.company_id })) as ApiCompany[];
		},
	});
	const companyOptions: ApiCompany[] = useMemo(() => companyOptionsQuery.data ?? [], [companyOptionsQuery.data]);

	/**
	 * HubSpot connection status for the Integrations tab. Gated to the tab so
	 * it only fetches when the tab is active (matching the old effect).
	 */
	const hubspotQuery = useQuery({
		queryKey: qk.hubspot.connection(),
		enabled: activeTab === 'integrations',
		queryFn: () => hubspotService.getSettings(),
	});
	const hubspotStatus: HubSpotConnectionStatus | null = hubspotQuery.data ?? null;
	const hubspotLoading = hubspotQuery.isLoading;
	/** Refresh the HubSpot status after connect/disconnect/sync actions. */
	const fetchHubspotStatus = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: qk.hubspot.all });
	}, [queryClient]);

	/**
	 * Roles the current user is allowed to assign. Fetched only while the user
	 * modal is open on the Users tab, so the picker only shows assignable roles.
	 */
	const availableRolesQuery = useQuery({
		queryKey: qk.users.list({ scope: 'available-roles' }),
		enabled: modalOpen && activeTab === 'users',
		queryFn: async () => {
			const res = await api.get<any>('/users/available-roles');
			/* The endpoint returns { data: [...role slugs...] } */
			return (Array.isArray(res.data) ? res.data : (res.data?.data ?? [])) as string[];
		},
	});
	const availableRoles: string[] = useMemo(() => availableRolesQuery.data ?? [], [availableRolesQuery.data]);

	/* CMS-managed Business Line tree — drives both the locations filter and the
	 * create/edit form's Business Line dropdown. Active-only, loaded once. */
	const businessLinesQuery = useQuery({
		queryKey: qk.businessLines.list({ active: true }),
		queryFn: async () => {
			const res = await businessLineService.list(true);
			return ((res.data as any)?.data ?? []) as BusinessLine[];
		},
	});
	const businessLines: BusinessLine[] = useMemo(() => businessLinesQuery.data ?? [], [businessLinesQuery.data]);

	/* Auto-dismiss success messages after 3 seconds */
	useEffect(() => {
		if (successMessage) {
			const timer = setTimeout(() => setSuccessMessage(null), 3000);
			return () => clearTimeout(timer);
		}
	}, [successMessage]);

	/* ============================================
	 * DUPLICATE DETECTION HELPERS
	 * ============================================ */

	/**
	 * Checks if a user with the given email already exists.
	 * Called with a 300ms debounce after the user types in the email field.
	 * Shows a yellow warning banner if a duplicate is found.
	 */
	const checkUserDuplicate = useCallback((email: string) => {
		// Clear any previous debounce timer
		if (duplicateCheckTimerRef.current) {
			clearTimeout(duplicateCheckTimerRef.current);
		}

		// Reset warning if email is empty or invalid
		if (!email.trim() || !isValidEmail(email.trim())) {
			setUserDuplicateWarning(null);
			return;
		}

		// Debounce the API call — wait 300ms after last keystroke
		duplicateCheckTimerRef.current = setTimeout(async () => {
			try {
				const res = await api.get<any>('/users/check-duplicate', { email: email.trim() });
				const data = res.data;
				if (data.exists) {
					// Build a warning message with the existing user's details
					const name = `${data.user.first_name} ${data.user.last_name}`;
					setUserDuplicateWarning(
						`A user with this email already exists: ${name} (${data.user.status}). Are you sure you want to continue?`
					);
				} else {
					setUserDuplicateWarning(null);
				}
			} catch {
				// Silently fail — don't block the form if the check fails
				setUserDuplicateWarning(null);
			}
		}, 300);
	}, []);

	/**
	 * Checks if a company with the given name already exists.
	 * Called with a 300ms debounce after the user types in the company name field.
	 */
	const checkCompanyDuplicate = useCallback((name: string) => {
		// Clear any previous debounce timer
		if (duplicateCheckTimerRef.current) {
			clearTimeout(duplicateCheckTimerRef.current);
		}

		// Reset warning if name is empty
		if (!name.trim()) {
			setCompanyDuplicateWarning(null);
			return;
		}

		// Debounce the API call — wait 300ms after last keystroke
		duplicateCheckTimerRef.current = setTimeout(async () => {
			try {
				const res = await api.get<any>('/companies/check-duplicate', { name: name.trim() });
				const data = res.data;
				if (data.exists) {
					setCompanyDuplicateWarning(
						`A company with this name already exists: ${data.company.name} (${data.company.status}). Are you sure you want to continue?`
					);
				} else {
					setCompanyDuplicateWarning(null);
				}
			} catch {
				// Silently fail — don't block the form if the check fails
				setCompanyDuplicateWarning(null);
			}
		}, 300);
	}, []);

	/**
	 * Checks if a location with the given name and address already exists.
	 * Called with a 300ms debounce after the user types in the name or address fields.
	 */
	const checkLocationDuplicate = useCallback((name: string, address: string) => {
		// Clear any previous debounce timer
		if (duplicateCheckTimerRef.current) {
			clearTimeout(duplicateCheckTimerRef.current);
		}

		// Reset warning if name is empty
		if (!name.trim()) {
			setLocationDuplicateWarning(null);
			return;
		}

		// Build query params — include address only if non-empty
		const params: Record<string, string> = { name: name.trim() };
		if (address.trim()) {
			params.address = address.trim();
		}

		// Debounce the API call — wait 300ms after last keystroke
		duplicateCheckTimerRef.current = setTimeout(async () => {
			try {
				const res = await api.get<any>('/locations/check-duplicate', params);
				const data = res.data;
				if (data.exists) {
					setLocationDuplicateWarning(
						`A building with this name${address.trim() ? ' and address' : ''} already exists: ${data.location.name}${data.location.address ? ` at ${data.location.address}` : ''}. Are you sure you want to continue?`
					);
				} else {
					setLocationDuplicateWarning(null);
				}
			} catch {
				// Silently fail — don't block the form if the check fails
				setLocationDuplicateWarning(null);
			}
		}, 300);
	}, []);

	/* ============================================
	 * CSV IMPORT HANDLERS
	 * ============================================ */

	/**
	 * Opens the CSV import modal for the specified entity type.
	 * Only available to admin/super_admin/developer roles.
	 */
	const openCsvImport = useCallback((entityType: ImportEntityType) => {
		setCsvImportEntity(entityType);
		setCsvImportOpen(true);
	}, []);

	/**
	 * Handles completion of a CSV import. Shows a success message
	 * and refreshes the data for the active tab.
	 */
	const handleCsvImportComplete = useCallback((result: ImportResult) => {
		// Show a success message with the import summary
		setSuccessMessage(
			`Import complete: ${result.imported} imported, ${result.skipped} skipped, ${result.errors.length} errors.`
		);
		// Refresh the data lists to show newly imported records. Invalidate every
		// entity cache CSV import can target (users/companies/contacts/locations).
		queryClient.invalidateQueries({ queryKey: qk.users.all });
		queryClient.invalidateQueries({ queryKey: qk.companies.all });
		queryClient.invalidateQueries({ queryKey: qk.contacts.all });
		queryClient.invalidateQueries({ queryKey: qk.locations.all });
	}, [queryClient]);

	/* ============================================
	 * TAB CHANGE HANDLER
	 * ============================================ */

	/** Switches the active tab and resets search/filter state */
	const handleTabChange = useCallback((tab: AdminTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setRoleFilter('');
		setStatusFilter('');
		setBusinessLineFilter('');
		setContactCompanyFilter('');
		setJobTypeFilter('all');
	}, []);

	/* ============================================
	 * MODAL HANDLERS
	 * ============================================ */

	/** Opens the create modal with empty form state.
	 *	When creating a user, also fetches the available roles so the
	 *	role picker only shows roles the current user can assign.
	 *	Clears any previous duplicate detection warnings. */
	const openCreateModal = useCallback(() => {
		setModalMode('create');
		setEditingId(null);
		setModalError(null);
		setFormErrors({});
		/* Reset all forms to empty defaults. The contact form pre-selects
		   the customer the list is currently filtered to (if any) so "filter
		   to a customer → add their contact" needs no re-picking. */
		setUserForm(EMPTY_USER_FORM);
		setCompanyForm(EMPTY_COMPANY_FORM);
		setContactForm({ ...EMPTY_CONTACT_FORM, company_id: contactCompanyFilter });
		setEditingContactCounts(null);
		setEditingContactOriginalCompanyId('');
		setLocationForm(EMPTY_LOCATION_FORM);
		/* Clear duplicate detection warnings from any previous modal session */
		setUserDuplicateWarning(null);
		setCompanyDuplicateWarning(null);
		setLocationDuplicateWarning(null);
		setModalOpen(true);
		/* The available-roles query auto-runs while the user modal is open on
		 * the Users tab (see availableRolesQuery `enabled`). */
	}, [contactCompanyFilter]);

	/** Opens the edit modal pre-filled with the selected record's data.
	 *	When editing a user, also fetches available roles and pre-selects
	 *	the user's current roles from the role_names array. */
	const openEditModal = useCallback((tab: AdminTab, record: any) => {
		setModalMode('edit');
		setEditingId(record.id);
		setModalError(null);
		setFormErrors({});

		if (tab === 'users') {
			/* Pre-fill user form with existing record data.
			   Use role_names (from the user_roles junction table) to populate
			   the multi-role checkbox group. Fall back to [role] if role_names
			   is not available (backward compatibility). */
			const currentRoles: string[] = Array.isArray(record.role_names) && record.role_names.length > 0
				? record.role_names
				: (record.role ? [record.role] : ['reviewer']);
			/* Extract additional company IDs from the company_ids array (from the
			   company_users junction table). Filter out the primary company_id
			   since it's already represented by the company_id field. */
			const primaryId = record.company_id ? String(record.company_id) : '';
			const allCompanyIds: string[] = Array.isArray(record.company_ids)
				? record.company_ids.map((id: any) => String(id))
				: [];
			const additionalIds = allCompanyIds.filter((id) => id !== primaryId);
			setUserForm({
				first_name: record.first_name || '',
				last_name: record.last_name || '',
				subcontractor_company: record.subcontractor_company || '',
				email: record.email || '',
				phone: record.phone || '',
				role: record.role || 'technician',
				roles: currentRoles,
				company_id: primaryId,
				additional_company_ids: additionalIds,
				can_view_financials: Boolean(record.can_view_financials),
				can_reassign: Boolean(record.can_reassign),
			});
			/* The available-roles query auto-runs while the user modal is open
			 * on the Users tab (see availableRolesQuery `enabled`). */
		} else if (tab === 'companies') {
			/* Pre-fill company form — physical address fields have been
			   moved to the locations table, only billing fields remain */
			setCompanyForm({
				name: record.name || '',
				phone: record.phone || '',
				billing_email: record.billing_email || record.email || '',
				billing_phone: record.billing_phone || '',
				tax_rate: record.tax_rate != null ? taxRateToPercent(record.tax_rate) : '',
				billing_terms: record.billing_terms || 'Net 30',
				notes: record.notes || '',
				billing_address: record.billing_address || '',
				billing_address_line_2: record.billing_address_line_2 || '',
				billing_city: record.billing_city || '',
				billing_state: record.billing_state || '',
				billing_zip: record.billing_zip || '',
			});
		} else if (tab === 'contacts') {
			/* Pre-fill contact form. The original company id + reference
			   counts feed the reassignment callout next to the customer
			   picker (counts come from the with_counts list fetch). */
			setContactForm({
				first_name: record.first_name || '',
				last_name: record.last_name || '',
				company_id: record.company_id != null ? String(record.company_id) : '',
				phone: record.phone || '',
				email: record.email || '',
				title: record.title || '',
				notes: record.notes || '',
			});
			setEditingContactOriginalCompanyId(record.company_id != null ? String(record.company_id) : '');
			setEditingContactCounts({
				workOrders: Number(record.work_orders_count ?? 0),
				inspections: Number(record.inspections_count ?? 0),
			});
		} else {
			/* Pre-fill location form with existing record data.
			   Extract company_ids from the companies pivot data. */
			const companyIds = Array.isArray(record.companies)
				? record.companies.map((c: any) => String(c.company_id))
				: (record.company_ids || []);
			setLocationForm({
				name: record.name || '',
				address: record.address || '',
				address_line_2: record.address_line_2 || '',
				city: record.city || '',
				state: record.state || '',
				zip: record.zip || '',
				cleanable_sqft: record.cleanable_sqft != null ? String(record.cleanable_sqft) : '',
				tax_rate: record.tax_rate != null ? taxRateToPercent(record.tax_rate) : '',
				business_line: record.business_line || '',
				notes: record.notes || '',
				company_ids: companyIds,
			});
		}

		setModalOpen(true);
	}, []);

	/** Closes the modal and clears all modal-related state, including
	 *	any duplicate detection warnings from the create form. */
	const closeModal = useCallback(() => {
		setModalOpen(false);
		setModalError(null);
		setFormErrors({});
		/* Clear duplicate detection warnings */
		setUserDuplicateWarning(null);
		setCompanyDuplicateWarning(null);
		setLocationDuplicateWarning(null);
		/* Clear the contact reassignment context */
		setEditingContactCounts(null);
		setEditingContactOriginalCompanyId('');
	}, []);

	/* ============================================
	 * FORM VALIDATION
	 * ============================================ */

	/**
	 * Validates the current form based on the active tab.
	 * Returns true if valid, false otherwise. Sets formErrors for inline display.
	 */
	const validateForm = useCallback((): boolean => {
		const errors: Record<string, string> = {};

		if (activeTab === 'users') {
			if (!userForm.first_name.trim()) errors.first_name = 'First name is required';
			if (!userForm.last_name.trim()) errors.last_name = 'Last name is required';
			/* A user with no role can sign in and see nothing, so the picker is
			   required here exactly as it is in the header's Add User modal. */
			if (userForm.roles.length === 0) errors.roles = 'Select at least one role';
			/* Email is the login identifier, so it's required for every role that
			   is an account. CSR / JSR / Other are assignable people rather than
			   accounts — a name is enough, and without an address they simply
			   can't sign in. Mirrors the same rule in UserController. */
			if (!userForm.email.trim()) {
				if (!rolesAllowMissingCredentials(userForm.roles)) errors.email = 'Email is required';
			} else if (!isValidEmail(userForm.email.trim())) {
				errors.email = 'Please enter a valid email address';
			}
		} else if (activeTab === 'companies') {
			if (!companyForm.name.trim()) errors.name = 'Company name is required';
		} else if (activeTab === 'contacts') {
			if (!contactForm.first_name.trim()) errors.first_name = 'First name is required';
			if (!contactForm.last_name.trim()) errors.last_name = 'Last name is required';
			if (!contactForm.company_id) errors.company_id = 'Customer is required';
			if (contactForm.email.trim() && !isValidEmail(contactForm.email.trim())) {
				errors.email = 'Please enter a valid email address';
			}
		} else {
			if (!locationForm.name.trim()) errors.name = 'Building name is required';
			/* A building with no customer link has no company_locations row, so it
			   never appears in the Building dropdown when creating a work order or
			   inspection. Requiring the link here stops invisible buildings from
			   being created in the first place. */
			if (locationForm.company_ids.length === 0) {
				errors.company_ids = 'Select at least one customer — buildings without one will not appear on work order or inspection forms';
			}
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	}, [activeTab, userForm, companyForm, contactForm, locationForm]);

	/* ============================================
	 * CRUD OPERATIONS
	 * ============================================ */

	/** Maps an admin tab to its query-key root for invalidation. */
	const tabKeyRoot = useCallback((tab: AdminTab) => {
		if (tab === 'users') return qk.users.all;
		if (tab === 'companies') return qk.companies.all;
		if (tab === 'contacts') return qk.contacts.all;
		return qk.locations.all;
	}, []);

	/** Maps an admin tab to its REST endpoint (shared by save + status mutations). */
	const tabEndpoint = (tab: AdminTab): string =>
		tab === 'users' ? '/users' : tab === 'companies' ? '/companies' : tab === 'contacts' ? '/contacts' : '/locations';

	/** Create/update mutation for the active entity. Branches on tab + mode in
	 *  the caller; here it just performs the request and invalidates on success. */
	const saveMutation = useMutation({
		mutationFn: (vars: { tab: AdminTab; mode: 'create' | 'edit'; id: string | number | null; payload: Record<string, any> }) => {
			const endpoint = tabEndpoint(vars.tab);
			return vars.mode === 'create'
				? api.post(endpoint, vars.payload)
				: api.patch(`${endpoint}/${vars.id}`, vars.payload);
		},
		onSuccess: (_data, vars) => {
			queryClient.invalidateQueries({ queryKey: tabKeyRoot(vars.tab) });
		},
	});

	/** Status-change mutation (deactivate/reactivate). */
	const statusMutation = useMutation({
		mutationFn: (vars: { tab: AdminTab; id: string | number; status: 'active' | 'inactive' }) => {
			return api.patch(`${tabEndpoint(vars.tab)}/${vars.id}`, { status: vars.status });
		},
		onSuccess: (_data, vars) => {
			queryClient.invalidateQueries({ queryKey: tabKeyRoot(vars.tab) });
		},
	});

	/**
	 * Handles form submission for both create and edit modes.
	 * Validates the form, builds the payload, and sends the appropriate
	 * API request (POST for create, PATCH for edit).
	 */
	const handleModalSubmit = useCallback(async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateForm()) return;

		setModalSaving(true);
		setModalError(null);

		try {
			if (activeTab === 'users') {
				/* Build user payload — password is auto-generated on the backend.
				   Include the roles array for multi-role assignment via the
				   user_roles junction table. The backend validates that the
				   requesting user has permission to assign each role. */
				const payload: Record<string, any> = {
					first_name: userForm.first_name.trim(),
					last_name: userForm.last_name.trim(),
					/* The business name belongs to subcontractors only — unticking
					   the role clears it rather than leaving a stale label. */
					subcontractor_company: userForm.roles.includes('subcontractor')
						? (userForm.subcontractor_company.trim() || null)
						: null,
					/* null, not '' — a blank email means "no sign-in", and null is
					   what keeps the unique index treating these users as distinct. */
					email: userForm.email.trim() || null,
					role: primaryRoleLabel(userForm.roles, userForm.role),
					roles: userForm.roles,
					can_view_financials: userForm.can_view_financials,
					can_reassign: userForm.can_reassign,
				};
				if (userForm.phone.trim()) payload.phone = userForm.phone.trim();
				if (userForm.company_id) payload.company_id = userForm.company_id;
				/* Include additional company IDs for multi-company association.
				   These are stored in the company_users junction table with
				   is_primary = false. Only include non-empty arrays. */
				if (userForm.additional_company_ids.length > 0) {
					payload.additional_company_ids = userForm.additional_company_ids.map(Number);
				}

				if (modalMode === 'create') {
					await saveMutation.mutateAsync({ tab: 'users', mode: 'create', id: null, payload });
					setSuccessMessage(userForm.email.trim()
						? 'User created successfully. They will receive an email with login credentials.'
						: 'User created successfully. Without an email address they can\'t sign in — they can still be assigned to work orders and inspections.');
					/* Jump back to page 1 so the freshly-created user (sorted to the top
					   by the backend's id-desc order) is guaranteed to be on screen */
					setUsersPage(1);
				} else {
					await saveMutation.mutateAsync({ tab: 'users', mode: 'edit', id: editingId, payload });
					setSuccessMessage('User updated successfully.');
				}
			} else if (activeTab === 'companies') {
				/* Build company payload — physical address fields are now in
				   the locations table, only billing fields remain here */
				const payload: Record<string, any> = {
					name: companyForm.name.trim(),
				};
				if (companyForm.phone.trim()) payload.phone = companyForm.phone.trim();
				if (companyForm.billing_email.trim()) payload.billing_email = companyForm.billing_email.trim();
				if (companyForm.billing_phone.trim()) payload.billing_phone = companyForm.billing_phone.trim();
				if (companyForm.tax_rate) payload.tax_rate = parseFloat(companyForm.tax_rate) / 100;
				if (companyForm.billing_terms) payload.billing_terms = companyForm.billing_terms;
				if (companyForm.notes.trim()) payload.notes = companyForm.notes.trim();

				/* Always send billing address fields */
				payload.billing_address = companyForm.billing_address.trim();
				payload.billing_address_line_2 = companyForm.billing_address_line_2.trim();
				payload.billing_city = companyForm.billing_city.trim();
				payload.billing_state = companyForm.billing_state.trim();
				payload.billing_zip = companyForm.billing_zip.trim();

				if (modalMode === 'create') {
					await saveMutation.mutateAsync({ tab: 'companies', mode: 'create', id: null, payload });
					setSuccessMessage('Company created successfully.');
					/* Company-list invalidation (in the mutation onSuccess) also
					   refreshes the company-options dropdown query. */
				} else {
					await saveMutation.mutateAsync({ tab: 'companies', mode: 'edit', id: editingId, payload });
					setSuccessMessage('Company updated successfully.');
				}
			} else if (activeTab === 'contacts') {
				/* Build contact payload. Optional text fields are sent as
				   null when blanked so a PATCH actually clears them (the
				   backend rules are nullable; an absent key is ignored). */
				const payload: Record<string, any> = {
					first_name: contactForm.first_name.trim(),
					last_name: contactForm.last_name.trim(),
					company_id: Number(contactForm.company_id),
					phone: contactForm.phone.trim() || null,
					email: contactForm.email.trim() || null,
					title: contactForm.title.trim() || null,
					notes: contactForm.notes.trim() || null,
				};

				if (modalMode === 'create') {
					await saveMutation.mutateAsync({ tab: 'contacts', mode: 'create', id: null, payload });
					setSuccessMessage('Contact created successfully.');
				} else {
					await saveMutation.mutateAsync({ tab: 'contacts', mode: 'edit', id: editingId, payload });
					/* Call out a reassignment distinctly — it's the action this
					   modal exists for and worth confirming loudly. */
					const wasReassigned = editingContactOriginalCompanyId
						&& contactForm.company_id !== editingContactOriginalCompanyId;
					if (wasReassigned) {
						const newCompany = companyOptions.find((c) => String(c.id) === contactForm.company_id);
						setSuccessMessage(`Contact reassigned to ${newCompany?.name ?? 'the selected customer'}.`);
					} else {
						setSuccessMessage('Contact updated successfully.');
					}
				}
			} else {
				/* Build location payload — includes address_line_2 for suite/dock/gate */
				const payload: Record<string, any> = {
					name: locationForm.name.trim(),
				};
				if (locationForm.address.trim()) payload.address = locationForm.address.trim();
				/* Include address_line_2 for suite/unit/dock details */
				if (locationForm.address_line_2.trim()) payload.address_line_2 = locationForm.address_line_2.trim();
				if (locationForm.city.trim()) payload.city = locationForm.city.trim();
				if (locationForm.state.trim()) payload.state = locationForm.state.trim();
				if (locationForm.zip.trim()) payload.zip = locationForm.zip.trim();
				if (locationForm.cleanable_sqft) payload.cleanable_sqft = parseInt(locationForm.cleanable_sqft, 10);
				if (locationForm.tax_rate) payload.tax_rate = parseFloat(locationForm.tax_rate) / 100;
				if (locationForm.business_line) payload.business_line = locationForm.business_line;
				if (locationForm.notes.trim()) payload.notes = locationForm.notes.trim();
				/* Include company_ids for pivot table linking */
				if (locationForm.company_ids.length > 0) payload.company_ids = locationForm.company_ids;

				if (modalMode === 'create') {
					await saveMutation.mutateAsync({ tab: 'locations', mode: 'create', id: null, payload });
					setSuccessMessage('Building created successfully.');
				} else {
					await saveMutation.mutateAsync({ tab: 'locations', mode: 'edit', id: editingId, payload });
					setSuccessMessage('Building updated successfully.');
				}
			}

			/* Close modal — the list refreshes via the mutation's cache invalidation */
			closeModal();
		} catch (err: any) {
			const msg = err?.data?.message || 'Failed to save record. Please check your input.';
			setModalError(msg);
		} finally {
			setModalSaving(false);
		}
	}, [activeTab, modalMode, editingId, userForm, companyForm, contactForm, locationForm, editingContactOriginalCompanyId, companyOptions, validateForm, closeModal, saveMutation]);

	/**
	 * Opens the deactivate confirmation modal for the target record.
	 * The actual API call happens in handleDeactivateConfirm.
	 */
	const handleDeactivate = useCallback((tab: AdminTab, id: string | number) => {
		setDeactivateTarget({ tab, id });
	}, []);

	/**
	 * Deactivates the record pending in the confirmation modal by setting
	 * its status to 'inactive'. Sends a PATCH request to the appropriate
	 * endpoint, then closes the modal.
	 */
	const handleDeactivateConfirm = useCallback(async () => {
		if (!deactivateTarget) return;

		setActionError(null);
		try {
			await statusMutation.mutateAsync({ tab: deactivateTarget.tab, id: deactivateTarget.id, status: 'inactive' });
			setSuccessMessage('Record deactivated successfully.');
		} catch (err: any) {
			setActionError(err?.data?.message || 'Failed to deactivate record.');
		} finally {
			setDeactivateTarget(null);
		}
	}, [deactivateTarget, statusMutation]);

	/**
	 * Reactivates a record by setting its status back to 'active'.
	 * Sends a PATCH request to the appropriate endpoint.
	 */
	const handleReactivate = useCallback(async (tab: AdminTab, id: string | number) => {
		setActionError(null);
		try {
			await statusMutation.mutateAsync({ tab, id, status: 'active' });
			setSuccessMessage('Record reactivated successfully.');
		} catch (err: any) {
			setActionError(err?.data?.message || 'Failed to reactivate record.');
		}
	}, [statusMutation]);

	/* ============================================
	 * IMPERSONATION HANDLER
	 * ============================================ */

	/**
	 * Opens the Reset Password modal for the target user.
	 * The actual API call happens in handleResetPasswordConfirm.
	 */
	const handleResetPassword = useCallback((targetUser: ApiUser) => {
		setResetPwTarget(targetUser);
		setResetPwValue('');
		setResetPwError(null);
		setResetPwModalOpen(true);
	}, []);

	/**
	 * Handles the confirm action from the Reset Password modal.
	 * Validates the temporary password and calls POST /users/{id}/reset-password.
	 * The target user will be forced to change their password on next login.
	 */
	/** Reset-password mutation. No list to invalidate — it just flips a
	 *  server-side "must change password" flag for the target user. */
	const resetPwMutation = useMutation({
		mutationFn: (vars: { id: string | number; password: string }) =>
			api.post(`/users/${vars.id}/reset-password`, { password: vars.password }),
	});

	const handleResetPasswordConfirm = useCallback(async () => {
		if (!resetPwTarget) return;

		// Client-side validation: min 12 characters
		if (resetPwValue.length < 12) {
			setResetPwError('Password must be at least 12 characters.');
			return;
		}

		setResetPwLoading(true);
		setResetPwError(null);

		try {
			await resetPwMutation.mutateAsync({ id: resetPwTarget.id, password: resetPwValue });
			setSuccessMessage(`Password reset for ${resetPwTarget.first_name} ${resetPwTarget.last_name}. They will be required to change it on next login.`);
			setResetPwModalOpen(false);
		} catch (err: any) {
			setResetPwError(err?.data?.message || 'Failed to reset password.');
		} finally {
			setResetPwLoading(false);
		}
	}, [resetPwTarget, resetPwValue, resetPwMutation]);

	/**
	 * Handles the "Sign In As" action for a user row.
	 * Shows a confirmation dialog, then calls the impersonate function
	 * from AuthContext. On success, redirects to the dashboard.
	 */
	const handleImpersonate = useCallback(async (targetUser: ApiUser) => {
		// Show a confirmation dialog before impersonating
		const confirmed = window.confirm(
			`You will be signed in as ${targetUser.first_name} ${targetUser.last_name}. Continue?`
		);
		if (!confirmed) return;

		try {
			// Call the impersonate function from AuthContext which handles
			// token swapping, localStorage updates, and state changes
			await impersonate(String(targetUser.id));
			// Redirect to dashboard after successful impersonation
			navigate('/');
		} catch (err: any) {
			setActionError(err?.data?.message || 'Failed to impersonate user. Please try again.');
		}
	}, [impersonate, navigate]);

	/* ============================================
	 * SHARED INPUT STYLING
	 * The strings live in utils/formClasses so this modal, the CMS tabs and
	 * the shared BuildingFieldset can't drift apart; these aliases keep the
	 * ~50 call sites below reading the way they always have.
	 * ============================================ */

	const inputClass = FORM_INPUT_CLASS;
	const selectClass = FORM_SELECT_CLASS;
	const labelClass = FORM_LABEL_CLASS;

	/* ============================================
	 * RENDER: USERS TAB
	 * ============================================ */

	/**
	 * Renders the Users tab content with responsive table/cards.
	 * Includes columns for Name, Email, Role, Company, Status, and Actions.
	 */
	const renderUsersTab = () => (
		<>
			{/* Desktop table — from the desktop breakpoint up. Below it (phones
			    AND iPads) the cards show instead: with the sidebar open an iPad
			    leaves ~470px here, and six columns plus actions scrolled sideways
			    and clipped the Role and Status columns. */}
			<div className="admin-page__table hidden desktop:block overflow-x-auto">
				<table className="w-full text-14px text-left">
					<thead className="border-b border-brand-border">
						<tr>
							{/* Email sits under the name rather than in its own column:
							    it was the widest cell in the row, and at the narrowest
							    desktop width (~620px beside the sidebar) its column
							    pushed Status and the actions off the right edge. */}
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Name / Email</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Role</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Company</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Status</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Actions</th>
						</tr>
					</thead>
					<tbody>
						{users.map((u) => {
							/* Derive the status string for badge rendering */
							const statusStr = u.status || 'active';
							const isInactive = statusStr === 'inactive';
							return (
								<tr key={u.id} className="border-b border-brand-border/50 hover:bg-brand-surface/50 transition-colors">
									<td className="px-12px py-8px text-brand-text font-medium">
										{u.first_name} {u.last_name}
										{/* Subcontractor's business, under their name. */}
										{u.subcontractor_company && <span className="admin-page__user-company block text-12px font-normal text-brand-text-muted">{u.subcontractor_company}</span>}
										{/* Email under the name; break-all so a long address wraps
										    instead of widening the column. */}
										<span className="admin-page__user-email block text-13px font-normal text-brand-text-muted break-all">{u.email || '—'}</span>
									</td>
									<td className="px-12px py-8px">
										{/* Display all assigned roles from the user_roles junction table.
											Falls back to the legacy single role column if role_names is empty. */}
										<span className="text-brand-text-muted">{(u.role_names && u.role_names.length > 0 ? u.role_names : [u.role]).map(formatRoleLabel).join(', ')}</span>
									</td>
									<td className="px-12px py-8px text-brand-text-muted"><UserCompanies user={u} /></td>
									<td className="px-12px py-8px">
										<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
											{statusStr}
										</span>
									</td>
									<td className="px-12px py-8px">
										<RowActions
											layout="row"
											isInactive={isInactive}
											onEdit={() => openEditModal('users', u)}
											onDeactivate={() => handleDeactivate('users', u.id)}
											onReactivate={() => handleReactivate('users', u.id)}
										>
											{/* Reset PW (admin+): a temporary password, changed at next sign-in.
											    Sign In As (super_admin): view the app as this user. */}
											{isSuperUser && <Button variant="quiet" size="sm" onClick={() => handleResetPassword(u)}>Reset PW</Button>}
											{isSuperAdmin && <Button variant="quiet" size="sm" onClick={() => handleImpersonate(u)}>Sign In As</Button>}
										</RowActions>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Card layout — phones and tablets (below desktop) */}
			<div className="admin-page__cards desktop:hidden space-y-12px">
				{users.map((u) => {
					const statusStr = u.status || 'active';
					const isInactive = statusStr === 'inactive';
					return (
						<div key={u.id} className="bg-white border border-brand-border rounded-lg p-16px">
							<div className="flex items-center justify-between mb-8px">
								<span className="font-medium text-brand-text">
									{u.first_name} {u.last_name}
									{u.subcontractor_company && <span className="admin-page__user-company block text-12px font-normal text-brand-text-muted">{u.subcontractor_company}</span>}
								</span>
								<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
									{statusStr}
								</span>
							</div>
							<p className="text-12px text-brand-text-muted">{u.email || '—'}</p>
							<p className="text-12px text-brand-text-muted mt-4px">Role: {(u.role_names && u.role_names.length > 0 ? u.role_names : [u.role]).map(formatRoleLabel).join(', ')}</p>
							<p className="text-12px text-brand-text-muted mt-4px"><UserCompanies user={u} labelled /></p>
							{/* Mobile action buttons */}
							<RowActions
								layout="card"
								isInactive={isInactive}
								onEdit={() => openEditModal('users', u)}
								onDeactivate={() => handleDeactivate('users', u.id)}
								onReactivate={() => handleReactivate('users', u.id)}
							>
								{isSuperUser && <Button variant="quiet" size="sm" onClick={() => handleResetPassword(u)}>Reset PW</Button>}
								{isSuperAdmin && <Button variant="quiet" size="sm" onClick={() => handleImpersonate(u)}>Sign In As</Button>}
							</RowActions>
						</div>
					);
				})}
			</div>

			{/* Pagination controls — only render when more than one page exists */}
			{usersLastPage > 1 && (
				<div className="flex flex-col gap-8px tablet:flex-row tablet:items-center tablet:justify-between mt-16px px-12px py-8px border-t border-brand-border">
					<span className="text-12px text-brand-text-muted">
						Page {usersPage} of {usersLastPage} ({usersTotal} total)
					</span>
					<div className="flex gap-8px">
						<button
							type="button"
							onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
							disabled={usersPage <= 1}
							className="px-12px py-6px min-h-44px text-12px border border-brand-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-surface/50"
						>
							Previous
						</button>
						<button
							type="button"
							onClick={() => setUsersPage((p) => Math.min(usersLastPage, p + 1))}
							disabled={usersPage >= usersLastPage}
							className="px-12px py-6px min-h-44px text-12px border border-brand-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-surface/50"
						>
							Next
						</button>
					</div>
				</div>
			)}
		</>
	);

	/* ============================================
	 * RENDER: COMPANIES TAB
	 * ============================================ */

	/**
	 * Renders the Companies tab content with responsive table/cards.
	 * Includes columns for Name, Phone, Billing Email, Status, Actions.
	 * Physical address is now managed via the Buildings tab.
	 */
	const renderCompaniesTab = () => (
		<>
			{/* Desktop table */}
			<div className="admin-page__table hidden desktop:block overflow-x-auto">
				<table className="w-full text-14px text-left">
					<thead className="border-b border-brand-border">
						<tr>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Name</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Phone</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Billing Email</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Status</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Actions</th>
						</tr>
					</thead>
					<tbody>
						{companies.map((c) => {
							/* Derive status string from is_active boolean or status field */
							const statusStr = c.status ?? (c.is_active !== false ? 'active' : 'inactive');
							const isInactive = statusStr === 'inactive';
							return (
								<tr key={c.id} className="border-b border-brand-border/50 hover:bg-brand-surface/50 transition-colors">
									<td className="px-12px py-8px text-brand-text font-medium">{c.name}</td>
									<td className="px-12px py-8px text-brand-text-muted">{c.phone || '--'}</td>
									<td className="px-12px py-8px text-brand-text-muted">{c.billing_email || c.email || '--'}</td>
									<td className="px-12px py-8px">
										<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
											{statusStr}
										</span>
									</td>
									<td className="px-12px py-8px">
										<RowActions
											layout="row"
											isInactive={isInactive}
											onEdit={() => openEditModal('companies', c)}
											onDeactivate={() => handleDeactivate('companies', c.id)}
											onReactivate={() => handleReactivate('companies', c.id)}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Card layout — phones and tablets (below desktop) */}
			<div className="admin-page__cards desktop:hidden space-y-12px">
				{companies.map((c) => {
					const statusStr = c.status ?? (c.is_active !== false ? 'active' : 'inactive');
					const isInactive = statusStr === 'inactive';
					return (
						<div key={c.id} className="bg-white border border-brand-border rounded-lg p-16px">
							<div className="flex items-center justify-between mb-8px">
								<span className="font-medium text-brand-text">{c.name}</span>
								<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
									{statusStr}
								</span>
							</div>
							{c.phone && <p className="text-12px text-brand-text-muted">{c.phone}</p>}
							<p className="text-12px text-brand-text-muted mt-4px">Billing: {c.billing_email || c.email || '--'}</p>
							<RowActions
								layout="card"
								isInactive={isInactive}
								onEdit={() => openEditModal('companies', c)}
								onDeactivate={() => handleDeactivate('companies', c.id)}
								onReactivate={() => handleReactivate('companies', c.id)}
							/>
						</div>
					);
				})}
			</div>
		</>
	);

	/* ============================================
	 * RENDER: CONTACTS TAB
	 * ============================================ */

	/** Total referencing records for a contact row ("3 WOs · 1 inspection"). */
	const contactLinkedSummary = (c: ApiContact): string => {
		const wo = Number(c.work_orders_count ?? 0);
		const insp = Number(c.inspections_count ?? 0);
		if (wo === 0 && insp === 0) return '--';
		const parts: string[] = [];
		if (wo > 0) parts.push(`${wo} WO${wo === 1 ? '' : 's'}`);
		if (insp > 0) parts.push(`${insp} inspection${insp === 1 ? '' : 's'}`);
		return parts.join(' · ');
	};

	const renderContactsTab = () => (
		<>
			{/* Desktop table */}
			<div className="admin-page__table hidden desktop:block overflow-x-auto">
				<table className="w-full text-14px text-left">
					<thead className="border-b border-brand-border">
						<tr>
							{/* Title sits under the name, and phone + email share one
							    "Reach" column — eight columns didn't fit beside the
							    sidebar at the narrowest desktop width. */}
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Name / Title</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Customer</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Phone / Email</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Linked Jobs</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Status</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Actions</th>
						</tr>
					</thead>
					<tbody>
						{contacts.map((c) => {
							const statusStr = c.status ?? 'active';
							const isInactive = statusStr === 'inactive';
							return (
								<tr key={c.id} className="border-b border-brand-border/50 hover:bg-brand-surface/50 transition-colors">
									<td className="px-12px py-8px text-brand-text font-medium">
										{c.full_name || `${c.first_name} ${c.last_name}`}
										{c.title && <span className="admin-page__contact-title block text-13px font-normal text-brand-text-muted">{c.title}</span>}
									</td>
									<td className="px-12px py-8px text-brand-text-muted">{c.company?.name || '--'}</td>
									<td className="px-12px py-8px text-brand-text-muted">
										{/* break-all so a long address wraps instead of widening the column. */}
										<span className="admin-page__contact-phone block whitespace-nowrap">{c.phone || '--'}</span>
										<span className="admin-page__contact-email block text-13px break-all">{c.email || '--'}</span>
									</td>
									<td className="px-12px py-8px text-brand-text-muted">{contactLinkedSummary(c)}</td>
									<td className="px-12px py-8px">
										<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
											{statusStr}
										</span>
									</td>
									<td className="px-12px py-8px">
										<RowActions
											layout="row"
											isInactive={isInactive}
											onEdit={() => openEditModal('contacts', c)}
											onDeactivate={() => handleDeactivate('contacts', c.id)}
											onReactivate={() => handleReactivate('contacts', c.id)}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Card layout — phones and tablets (below desktop) */}
			<div className="admin-page__cards desktop:hidden space-y-12px">
				{contacts.map((c) => {
					const statusStr = c.status ?? 'active';
					const isInactive = statusStr === 'inactive';
					return (
						<div key={c.id} className="bg-white border border-brand-border rounded-lg p-16px">
							<div className="flex items-center justify-between mb-8px">
								<span className="font-medium text-brand-text">{c.full_name || `${c.first_name} ${c.last_name}`}</span>
								<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
									{statusStr}
								</span>
							</div>
							<p className="text-12px text-brand-text-muted">{c.company?.name || 'No customer'}{c.title ? ` — ${c.title}` : ''}</p>
							{c.phone && <p className="text-12px text-brand-text-muted mt-4px">{c.phone}</p>}
							{c.email && <p className="text-12px text-brand-text-muted mt-4px">{c.email}</p>}
							<p className="text-12px text-brand-text-muted mt-4px">Linked: {contactLinkedSummary(c)}</p>
							<RowActions
								layout="card"
								isInactive={isInactive}
								onEdit={() => openEditModal('contacts', c)}
								onDeactivate={() => handleDeactivate('contacts', c.id)}
								onReactivate={() => handleReactivate('contacts', c.id)}
							/>
						</div>
					);
				})}
			</div>
		</>
	);

	/* ============================================
	 * RENDER: LOCATIONS TAB
	 * ============================================ */

	/**
	 * Renders the Buildings tab content with responsive table/cards.
	 * Includes columns for Name, Address, Companies, Business Line, Sq Ft, Status, Actions.
	 */
	const renderLocationsTab = () => (
		<>
			{/* Desktop table */}
			<div className="admin-page__table hidden desktop:block overflow-x-auto">
				<table className="w-full text-14px text-left">
					<thead className="border-b border-brand-border">
						<tr>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Name</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Address</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Companies</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Business Line</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Status</th>
							<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Actions</th>
						</tr>
					</thead>
					<tbody>
						{locations.map((loc) => {
							const statusStr = loc.status ?? (loc.is_active !== false ? 'active' : 'inactive');
							const isInactive = statusStr === 'inactive';
							/* Derive deduped, alphabetically sorted company names */
							const companyNames = loc.companies && loc.companies.length > 0
								? dedupeAndSortStrings(loc.companies.map((c) => c.name)).join(', ')
								: '--';
							return (
								<tr key={loc.id} className="border-b border-brand-border/50 hover:bg-brand-surface/50 transition-colors">
									<td className="px-12px py-8px text-brand-text font-medium">{loc.name}</td>
									{/* Address parts are nullable — join whatever is present, '--' when none */}
									<td className="px-12px py-8px text-brand-text-muted">{[loc.address, loc.city, loc.state].filter(Boolean).join(', ') || '--'}</td>
									<td className="px-12px py-8px text-brand-text-muted">{companyNames}</td>
									<td className="px-12px py-8px text-brand-text-muted capitalize">{loc.business_line?.replace(/_/g, ' ') || '--'}</td>
									<td className="px-12px py-8px">
										<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
											{statusStr}
										</span>
									</td>
									<td className="px-12px py-8px">
										<RowActions
											layout="row"
											isInactive={isInactive}
											onEdit={() => openEditModal('locations', loc)}
											onDeactivate={() => handleDeactivate('locations', loc.id)}
											onReactivate={() => handleReactivate('locations', loc.id)}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{/* Card layout — phones and tablets (below desktop) */}
			<div className="admin-page__cards desktop:hidden space-y-12px">
				{locations.map((loc) => {
					const statusStr = loc.status ?? (loc.is_active !== false ? 'active' : 'inactive');
					const isInactive = statusStr === 'inactive';
					return (
						<div key={loc.id} className="bg-white border border-brand-border rounded-lg p-16px">
							<div className="flex items-center justify-between mb-8px">
								<span className="font-medium text-brand-text">{loc.name}</span>
								<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getStatusBadgeClass(statusStr)}`}>
									{statusStr}
								</span>
							</div>
							{/* Address parts are nullable — only render the line when something is present */}
							{(loc.address || loc.city || loc.state) && (
								<p className="text-12px text-brand-text-muted">{[loc.address, loc.city, loc.state].filter(Boolean).join(', ')}</p>
							)}
							{/* Show associated company names — deduped and sorted A-Z */}
							{loc.companies && loc.companies.length > 0 && (
								<p className="text-12px text-brand-text-muted mt-4px">
									{loc.companies.length === 1 ? 'Company' : 'Companies'}: {dedupeAndSortStrings(loc.companies.map((c) => c.name)).join(', ')}
								</p>
							)}
							{loc.business_line && (
								<p className="text-12px text-brand-text-muted mt-4px capitalize">{loc.business_line.replace(/_/g, ' ')}</p>
							)}
							{loc.cleanable_sqft && (
								<p className="text-12px text-brand-text-muted mt-4px">{loc.cleanable_sqft.toLocaleString()} sq ft</p>
							)}
							<RowActions
								layout="card"
								isInactive={isInactive}
								onEdit={() => openEditModal('locations', loc)}
								onDeactivate={() => handleDeactivate('locations', loc.id)}
								onReactivate={() => handleReactivate('locations', loc.id)}
							/>
						</div>
					);
				})}
			</div>
		</>
	);

	/* ============================================
	 * RENDER: JOBS TAB
	 * ============================================ */

	/**
	 * Returns a Tailwind badge class string for a job type label.
	 * Work Orders get a blue badge, Inspections get a purple badge.
	 */
	const getJobTypeBadgeClass = (jobType: string): string => {
		return jobType === 'Work Order'
			? 'bg-blue-100 text-blue-800'
			: 'bg-purple-100 text-purple-800';
	};

	/**
	 * Returns a Tailwind badge class string for a job status label.
	 * Maps common status values to appropriate color schemes.
	 */
	const getJobStatusBadgeClass = (status: string): string => {
		const s = status?.toLowerCase() || '';
		if (s === 'completed' || s === 'passed') return 'bg-green-100 text-green-800';
		if (s === 'in_progress' || s === 'in progress') return 'bg-yellow-100 text-yellow-800';
		if (s === 'failed' || s === 'cancelled') return 'bg-red-100 text-red-800';
		if (s === 'open' || s === 'pending') return 'bg-blue-100 text-blue-800';
		return 'bg-gray-100 text-gray-600';
	};

	/**
	 * Formats a status slug into a human-readable label (e.g., "in_progress" => "In Progress").
	 */
	const formatStatus = (status: string): string => status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

	/**
	 * Filters the jobs array by the current jobTypeFilter and searchQuery.
	 * Search checks subject, companyName, assigneeName, and status.
	 */
	const filteredJobs = jobs.filter((job) => {
		/* Apply type filter */
		if (jobTypeFilter === 'work_order' && job.jobType !== 'Work Order') return false;
		if (jobTypeFilter === 'inspection' && job.jobType !== 'Inspection') return false;
		/* Apply search filter across multiple fields */
		if (searchQuery.trim()) {
			const q = searchQuery.trim().toLowerCase();
			const searchable = `${job.subject} ${job.companyName} ${job.assigneeName} ${job.status} ${job.jobType}`.toLowerCase();
			if (!searchable.includes(q)) return false;
		}
		return true;
	});

	/**
	 * Renders the Jobs tab content with a combined table of Work Orders and Inspections.
	 * Includes type filter buttons, search, and clickable rows that navigate to detail pages.
	 */
	const renderJobsTab = () => (
		<>
			{/* Type filter buttons — All, Work Orders, Inspections */}
			<div className="flex items-center gap-8px mb-16px">
				{([
					{ key: 'all' as JobTypeFilter, label: 'All' },
					{ key: 'work_order' as JobTypeFilter, label: 'Work Orders' },
					{ key: 'inspection' as JobTypeFilter, label: 'Inspections' },
				]).map((opt) => (
					<button key={opt.key} type="button" onClick={() => setJobTypeFilter(opt.key)} className={`inline-flex items-center px-12px py-6px min-h-44px desktop:min-h-0 rounded-full text-12px font-medium transition-colors ${jobTypeFilter === opt.key ? 'bg-brand-primary text-white' : 'bg-gray-100 text-brand-text-muted hover:bg-gray-200'}`}>
						{opt.label}
					</button>
				))}
			</div>

			{/* Loading state for jobs data */}
			{jobsLoading && (
				<div className="flex items-center justify-center py-32px">
					<p className="text-brand-text-subtle text-14px">Loading jobs...</p>
				</div>
			)}

			{/* Desktop table — from the desktop breakpoint up. Below it (phones
			    AND iPads) the cards show instead: with the sidebar open an iPad
			    leaves ~470px here, and six columns plus actions scrolled sideways
			    and clipped the Role and Status columns. */}
			{!jobsLoading && (
				<div className="admin-page__table hidden desktop:block overflow-x-auto">
					<table className="w-full text-14px text-left">
						<thead className="border-b border-brand-border">
							<tr>
								<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Type</th>
								<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">ID</th>
								<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Subject / Company</th>
								<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Assignee</th>
								<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Status</th>
								<th className="px-12px py-8px font-medium text-brand-text-muted uppercase tracking-wide text-12px">Date</th>
							</tr>
						</thead>
						<tbody>
							{filteredJobs.map((job) => (
								<tr key={`${job.jobType}-${job.id}`} onClick={() => navigate(job.jobType === 'Work Order' ? `/work-orders/${job.id}` : `/inspections/${job.id}`)} className="border-b border-brand-border/50 hover:bg-brand-surface/50 transition-colors cursor-pointer">
									<td className="px-12px py-8px">
										<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getJobTypeBadgeClass(job.jobType)}`}>{job.jobType}</span>
									</td>
									<td className="px-12px py-8px text-brand-text-muted">#{String(job.id)}</td>
									{/* Company under the subject, not its own column, so the
									    table fits beside the sidebar at the narrowest desktop width. */}
									<td className="px-12px py-8px text-brand-text font-medium">
										{job.subject}
										<span className="admin-page__job-company block text-13px font-normal text-brand-text-muted">{job.companyName}</span>
									</td>
									<td className="px-12px py-8px text-brand-text-muted">{job.assigneeName}</td>
									<td className="px-12px py-8px">
										<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getJobStatusBadgeClass(job.status)}`}>{formatStatus(job.status)}</span>
									</td>
									<td className="px-12px py-8px text-brand-text-muted">{formatJobDate(job)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Card layout — phones and tablets (below desktop) */}
			{!jobsLoading && (
				<div className="admin-page__cards desktop:hidden space-y-12px">
					{filteredJobs.map((job) => (
						<div key={`${job.jobType}-${job.id}`} onClick={() => navigate(job.jobType === 'Work Order' ? `/work-orders/${job.id}` : `/inspections/${job.id}`)} className="bg-white border border-brand-border rounded-lg p-16px cursor-pointer hover:shadow-md transition-shadow">
							<div className="flex items-center justify-between mb-8px">
								<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getJobTypeBadgeClass(job.jobType)}`}>{job.jobType}</span>
								<span className={`inline-block px-8px py-2px text-12px font-semibold rounded-full ${getJobStatusBadgeClass(job.status)}`}>{formatStatus(job.status)}</span>
							</div>
							<p className="font-medium text-brand-text">{job.subject}</p>
							<p className="text-12px text-brand-text-muted mt-4px">#{String(job.id)} | {job.companyName}</p>
							<p className="text-12px text-brand-text-muted mt-4px">Assignee: {job.assigneeName}</p>
							<p className="text-12px text-brand-text-muted mt-4px">{formatJobDate(job)}</p>
						</div>
					))}
				</div>
			)}

			{/* Empty state when no jobs match the current filters */}
			{!jobsLoading && filteredJobs.length === 0 && (
				<p className="text-brand-text-subtle text-center py-32px text-14px">
					No jobs found. {searchQuery ? 'Try a different search term.' : 'No work orders or inspections available.'}
				</p>
			)}
		</>
	);

	/* ============================================
	 * RENDER: MODAL FORMS
	 * ============================================ */

	/**
	 * Renders an inline validation error message below a form field.
	 * Only renders if the specified field key has an error.
	 */
	const renderFieldError = (fieldKey: string) => {
		if (!formErrors[fieldKey]) return null;
		return <p className="mt-4px text-12px text-red-600">{formErrors[fieldKey]}</p>;
	};

	/**
	 * Renders the User modal form body.
	 * Includes fields for name, email, phone, role, company, and permission toggles.
	 * Email and primary company are only required for roles that sign in — CSR /
	 * JSR / Other are created from a name alone.
	 */
	const renderUserForm = () => {
		/* CSR / JSR / Other need nothing but a name, so their credential fields
		   lose the required marker. Any other checked role makes this an account
		   again and the marker comes back. */
		const emailIsOptional = rolesAllowMissingCredentials(userForm.roles);

		return (
		<div className="space-y-12px">
			{/* First Name (required) */}
			<div>
				<label htmlFor="modal-first-name" className={labelClass}>
					First Name <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					id="modal-first-name"
					value={userForm.first_name}
					onChange={(e) => setUserForm((prev) => ({ ...prev, first_name: e.target.value }))}
					className={`${inputClass} ${formErrors.first_name ? 'border-red-500' : ''}`}
					placeholder="Enter first name"
				/>
				{renderFieldError('first_name')}
			</div>

			{/* Last Name (required) */}
			<div>
				<label htmlFor="modal-last-name" className={labelClass}>
					Last Name <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					id="modal-last-name"
					value={userForm.last_name}
					onChange={(e) => setUserForm((prev) => ({ ...prev, last_name: e.target.value }))}
					className={`${inputClass} ${formErrors.last_name ? 'border-red-500' : ''}`}
					placeholder="Enter last name"
				/>
				{renderFieldError('last_name')}
			</div>

			{/* Email — required for account roles, optional for CSR / JSR / Other.
			   Triggers a duplicate check on blur/change. */}
			<div>
				<label htmlFor="modal-email" className={labelClass}>
					Email {emailIsOptional ? <span className="text-brand-text-subtle">(optional)</span> : <span className="text-red-500">*</span>}
				</label>
				<input
					type="email"
					id="modal-email"
					value={userForm.email}
					onChange={(e) => {
						setUserForm((prev) => ({ ...prev, email: e.target.value }));
						/* Trigger debounced duplicate check as the user types */
						if (modalMode === 'create') checkUserDuplicate(e.target.value);
					}}
					onBlur={() => {
						/* Also check on blur in case the user pastes and tabs away */
						if (modalMode === 'create') checkUserDuplicate(userForm.email);
					}}
					className={`${inputClass} ${formErrors.email ? 'border-red-500' : ''}`}
					placeholder="user@example.com"
				/>
				{renderFieldError('email')}
				{/* Say out loud what leaving it blank costs, so nobody has to infer
				   that "optional email" also means "cannot sign in". */}
				{emailIsOptional && !userForm.email.trim() && (
					<p className="text-12px text-brand-text-subtle italic mt-4px">
						Without an email this person can't sign in. They can still be assigned to work orders and inspections.
					</p>
				)}
				{/* Duplicate warning banner — yellow, non-blocking */}
				{modalMode === 'create' && userDuplicateWarning && (
					<Alert variant="warning" size="sm" className="mt-4px">{userDuplicateWarning}</Alert>
				)}
			</div>

			{/* Phone (optional) */}
			<div>
				<label htmlFor="modal-phone" className={labelClass}>Phone</label>
				<input
					type="tel"
					id="modal-phone"
					value={userForm.phone}
					onChange={(e) => setUserForm((prev) => ({ ...prev, phone: formatPhone(e.target.value) }))}
					className={inputClass}
					placeholder="(555) 123-4567"
				/>
			</div>

			{/* Role assignment — the shared multi-select picker, identical to the
				one behind the header's Add User button. Shows only the roles the
				current user is permitted to assign (GET /users/available-roles);
				each checked role lands in userForm.roles and is synced to the
				user_roles junction table by the backend. */}
			<RoleSelector
				options={availableRoles}
				value={userForm.roles}
				onChange={(roles) => setUserForm((prev) => ({ ...prev, roles }))}
				loading={availableRolesQuery.isLoading}
				error={formErrors.roles}
				className="admin-page__roles"
				dataRef="page-admin-page-roles"
				optionDataRef="page-admin-page-role-option"
			/>

			{/* Subcontractor company — appears once the Subcontractor role is
			   ticked. Beta testers had been typing the vendor ("Cyclone Window
			   Cleaning") into the name fields because there was nowhere else for
			   it; now the person's own name goes above and their business here,
			   and pickers show "Name — Business (Subcontractor)". */}
			{userForm.roles.includes('subcontractor') && (
				<div className="admin-page__subcontractor-company">
					<label htmlFor="modal-subcontractor-company" className={labelClass}>Subcontractor Company</label>
					<input
						type="text"
						id="modal-subcontractor-company"
						value={userForm.subcontractor_company}
						onChange={(e) => setUserForm((prev) => ({ ...prev, subcontractor_company: e.target.value }))}
						className={inputClass}
						placeholder="e.g. Cyclone Window Cleaning"
					/>
					<p className="text-12px text-brand-text-subtle mt-4px">
						The business they work for. Shown next to their name wherever people are picked.
					</p>
				</div>
			)}

			{/* Primary Company (required) — the user's main company affiliation,
			   stored in both the users.company_id column and the company_users
			   junction table with is_primary = true.

			   Searchable, like the Additional Companies picker right below it:
			   a plain <select> of every customer on the account is the same
			   scroll hunt, and having the two neighbouring company fields
			   behave differently is worse than either choice on its own.
			   SearchableSelect dedupes and alphabetizes the options itself. */}
			<div>
				<label htmlFor="modal-company" className={labelClass}>
					Primary Company {emailIsOptional ? <span className="text-brand-text-subtle">(optional)</span> : <span className="text-red-500">*</span>}
				</label>
				<SearchableSelect
					id="modal-company"
					options={companyOptions.map((co) => ({ value: String(co.id), label: co.name }))}
					value={userForm.company_id}
					onChange={(newPrimaryId) => {
						setUserForm((prev) => ({
							...prev,
							company_id: newPrimaryId,
							/* Remove the new primary company from additional companies
							   if it was previously selected as an additional company */
							additional_company_ids: prev.additional_company_ids.filter((id) => id !== newPrimaryId),
						}));
					}}
					placeholder="Select primary company"
				/>
			</div>

			{/* Additional Companies — search-and-add picker for associating the user
			   with companies beyond their primary. The primary company is excluded from
			   the option list since it is already set above. Each added company is stored
			   in the company_users junction table with is_primary = false.

			   This was a scrolling list of checkboxes, which stops working at the point
			   an account has more companies than fit the box: the admin scrolls hunting
			   for a name they could have typed. Typing filters; the pills underneath say
			   who is already on the user without scrolling back up. */}
			<div className="border border-brand-border rounded-md p-12px">
				<p className="text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">Additional Companies (optional)</p>
				{/* Filter out the primary company from the list — it's already the primary */}
				{companyOptions.filter((co) => String(co.id) !== userForm.company_id).length === 0 ? (
					<p className="text-12px text-brand-text-subtle italic">No additional companies available.</p>
				) : (
					<SearchableMultiSelect
						id="modal-additional-companies"
						options={dedupeAndSort(companyOptions.filter((co) => String(co.id) !== userForm.company_id).map((co) => ({ value: String(co.id), label: co.name })))}
						selectedValues={userForm.additional_company_ids}
						onChange={(next) => setUserForm((prev) => ({ ...prev, additional_company_ids: next }))}
						placeholder="Search companies..."
						emptyText="No additional companies yet — search above to add one."
						allSelectedText="Every other company is already on this user."
						className="admin-page__company-picker"
						dataRef="page-admin-page-company-picker"
					/>
				)}
			</div>

			{/* Permission toggles — grouped in a bordered section */}
			<div className="border border-brand-border rounded-md p-12px">
				<p className="text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">Permissions</p>
				{/* Same stacking fix as the roles group above: inline-flex labels
				    need a flex column to sit one per line instead of wrapping. Each
				    says what it does; with a description under it the box sits
				    beside the first line, like the roles. There is no Company Admin
				    box: the company_admin role replaced users.is_company_admin (the
				    user_roles migration copied it across once), and nothing has read
				    the column since, so a box for it changed nothing. */}
				<div className="flex flex-col gap-12px">
					{/* can_view_financials: WorkOrderDetailPage's Billing tab */}
					<Checkbox checked={userForm.can_view_financials} onChange={(checked) => setUserForm((prev) => ({ ...prev, can_view_financials: checked }))} label="Can View Financials" description="Shows the Billing tab on work orders, with pricing and billing details." className="admin-page__permission flex-shrink-0" dataRef="page-admin-page-permission" />
					{/* can_reassign: enforced by the server, see WorkOrderController::update */}
					<Checkbox checked={userForm.can_reassign} onChange={(checked) => setUserForm((prev) => ({ ...prev, can_reassign: checked }))} label="Can Reassign Work Orders" description="Can move a job to someone else: the Assign button on the Work Orders list, and Assign To when editing." className="admin-page__permission flex-shrink-0" dataRef="page-admin-page-permission" />
				</div>
			</div>

			{/* Note about password auto-generation (create mode only). A password is
			   only generated when there's an email to send it to — otherwise there
			   would be a secret nobody could ever use. */}
			{modalMode === 'create' && userForm.email.trim() && (
				<p className="text-12px text-brand-text-subtle italic">
					A password will be auto-generated and the user will receive an email with login credentials.
				</p>
			)}
		</div>
		);
	};

	/**
	 * Renders the Company modal form body.
	 * Physical address is managed via the Buildings tab.
	 * Includes fields for name, phone, billing info, tax rate, billing address, and notes.
	 */
	const renderCompanyForm = () => (
		<div className="space-y-12px">
			{/* Company Name (required) — triggers duplicate check on blur/change */}
			<div>
				<label htmlFor="modal-company-name" className={labelClass}>
					Company Name <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					id="modal-company-name"
					value={companyForm.name}
					onChange={(e) => {
						setCompanyForm((prev) => ({ ...prev, name: e.target.value }));
						/* Trigger debounced duplicate check as the user types */
						if (modalMode === 'create') checkCompanyDuplicate(e.target.value);
					}}
					onBlur={() => {
						/* Also check on blur in case the user pastes and tabs away */
						if (modalMode === 'create') checkCompanyDuplicate(companyForm.name);
					}}
					className={`${inputClass} ${formErrors.name ? 'border-red-500' : ''}`}
					placeholder="Acme Corp"
				/>
				{renderFieldError('name')}
				{/* Duplicate warning banner — yellow, non-blocking */}
				{modalMode === 'create' && companyDuplicateWarning && (
					<Alert variant="warning" size="sm" className="mt-4px">{companyDuplicateWarning}</Alert>
				)}
			</div>

			{/* Hint: physical address is managed on the Buildings tab */}
			<p className="text-12px text-brand-text-subtle italic">
				Physical/office address is managed in the Buildings tab (type &quot;Office&quot;).
			</p>

			{/* Phone */}
			<div>
				<label htmlFor="modal-company-phone" className={labelClass}>Phone</label>
				<input
					type="tel"
					id="modal-company-phone"
					value={companyForm.phone}
					onChange={(e) => setCompanyForm((prev) => ({ ...prev, phone: formatPhone(e.target.value) }))}
					className={inputClass}
					placeholder="(555) 123-4567"
				/>
			</div>

			{/* Billing Email + Billing Phone — the accounts-payable contact details.
				Both flow onto every work order's billing block for this customer,
				so they only have to be entered once here. */}
			<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
				<div>
					<label htmlFor="modal-company-billing-email" className={labelClass}>Billing Email</label>
					<input
						type="email"
						id="modal-company-billing-email"
						value={companyForm.billing_email}
						onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_email: e.target.value }))}
						className={inputClass}
						placeholder="billing@acme.com"
					/>
				</div>
				<div>
					<label htmlFor="modal-company-billing-phone" className={labelClass}>Billing Phone</label>
					<input
						type="tel"
						id="modal-company-billing-phone"
						value={companyForm.billing_phone}
						onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_phone: formatPhone(e.target.value) }))}
						className={inputClass}
						placeholder="(555) 123-4567"
					/>
				</div>
			</div>

			{/* Tax Rate (percentage) and Billing Terms on the same row */}
			<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
				<div>
					<label htmlFor="modal-company-tax-rate" className={labelClass}>Tax Rate (%)</label>
					<input
						type="number"
						id="modal-company-tax-rate"
						value={companyForm.tax_rate}
						onChange={(e) => setCompanyForm((prev) => ({ ...prev, tax_rate: e.target.value }))}
						className={inputClass}
						placeholder="7.0"
						step="0.01"
						min="0"
						max="100"
					/>
				</div>
				<div>
					<label htmlFor="modal-company-billing-terms" className={labelClass}>Billing Terms</label>
					<select
						id="modal-company-billing-terms"
						value={companyForm.billing_terms}
						onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_terms: e.target.value }))}
						className={selectClass}
					>
						{BILLING_TERMS_OPTIONS.map((bt) => (
							<option key={bt} value={bt}>{bt}</option>
						))}
					</select>
				</div>
			</div>

			{/* Notes */}
			<div>
				<label htmlFor="modal-company-notes" className={labelClass}>Notes</label>
				<textarea id="modal-company-notes" value={companyForm.notes} onChange={(e) => setCompanyForm((prev) => ({ ...prev, notes: e.target.value }))} className={inputClass} rows={3} placeholder="Additional notes about this company..." />
			</div>

			{/* ── Billing Address Section ──────────────────────────────────────
				The company's billing/invoice mailing address. Always shown since
				physical address is now managed in the Buildings tab. */}
			<div className="border border-brand-border rounded-md p-12px space-y-8px">
				<p className="text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">Billing Address</p>
				{/* Billing Address Line 1 — uses AddressInput with Google Places autocomplete.
					When a suggestion is selected, billing city/state/zip are auto-filled. */}
				<div>
					<label htmlFor="modal-company-billing-address" className={labelClass}>Address</label>
					<AddressInput
						id="modal-company-billing-address"
						value={companyForm.billing_address}
						onChange={(val) => setCompanyForm((prev) => ({ ...prev, billing_address: val }))}
						onSelect={(parsed) => {
							/* Auto-fill billing address fields from Google Places selection */
							setCompanyForm((prev) => ({
								...prev,
								billing_address: parsed.street,
								billing_city: parsed.city,
								billing_state: parsed.state,
								billing_zip: parsed.zip,
							}));
						}}
						placeholder="Start typing billing address..."
					/>
				</div>
				{/* Billing Address Line 2 */}
				<div>
					<label htmlFor="modal-company-billing-address-line-2" className={labelClass}>Address Line 2</label>
					<input type="text" id="modal-company-billing-address-line-2" value={companyForm.billing_address_line_2} onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_address_line_2: e.target.value }))} className={inputClass} placeholder="Suite, dept, attn..." />
				</div>
				{/* Billing City, State, ZIP — auto-filled by AddressInput.onSelect, still manually editable */}
				<div className="grid grid-cols-1 tablet:grid-cols-3 gap-8px">
					<div>
						<label htmlFor="modal-company-billing-city" className={labelClass}>City</label>
						<input type="text" id="modal-company-billing-city" value={companyForm.billing_city} onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_city: e.target.value }))} className={inputClass} placeholder="City" />
					</div>
					<div>
						<label htmlFor="modal-company-billing-state" className={labelClass}>State</label>
						<input type="text" id="modal-company-billing-state" value={companyForm.billing_state} onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_state: e.target.value }))} className={inputClass} placeholder="ST" maxLength={2} />
					</div>
					<div>
						<label htmlFor="modal-company-billing-zip" className={labelClass}>ZIP</label>
						<input type="text" id="modal-company-billing-zip" value={companyForm.billing_zip} onChange={(e) => setCompanyForm((prev) => ({ ...prev, billing_zip: e.target.value }))} className={inputClass} placeholder="ZIP" maxLength={10} />
					</div>
				</div>
			</div>
		</div>
	);

	/**
	 * Renders the Contact modal form body.
	 * The Customer select is the reassignment control: picking a different
	 * customer on an existing contact moves the contact to that customer's
	 * database. A callout summarizes what the move affects (historical WOs
	 * and inspections keep their link; the contact stops appearing in the
	 * old customer's pickers).
	 */
	const renderContactForm = () => {
		const isReassigning = modalMode === 'edit'
			&& Boolean(editingContactOriginalCompanyId)
			&& Boolean(contactForm.company_id)
			&& contactForm.company_id !== editingContactOriginalCompanyId;
		const linkedTotal = (editingContactCounts?.workOrders ?? 0) + (editingContactCounts?.inspections ?? 0);

		return (
			<div className="space-y-12px">
				{/* First + Last Name (required) */}
				<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
					<div>
						<label htmlFor="modal-contact-first-name" className={labelClass}>
							First Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="modal-contact-first-name"
							value={contactForm.first_name}
							onChange={(e) => setContactForm((prev) => ({ ...prev, first_name: e.target.value }))}
							className={`${inputClass} ${formErrors.first_name ? 'border-red-500' : ''}`}
							placeholder="Jane"
						/>
						{renderFieldError('first_name')}
					</div>
					<div>
						<label htmlFor="modal-contact-last-name" className={labelClass}>
							Last Name <span className="text-red-500">*</span>
						</label>
						<input
							type="text"
							id="modal-contact-last-name"
							value={contactForm.last_name}
							onChange={(e) => setContactForm((prev) => ({ ...prev, last_name: e.target.value }))}
							className={`${inputClass} ${formErrors.last_name ? 'border-red-500' : ''}`}
							placeholder="Roe"
						/>
						{renderFieldError('last_name')}
					</div>
				</div>

				{/* Customer (required) — THE reassignment control in edit mode */}
				<div>
					<label htmlFor="modal-contact-company" className={labelClass}>
						Customer <span className="text-red-500">*</span>
					</label>
					<select
						id="modal-contact-company"
						value={contactForm.company_id}
						onChange={(e) => setContactForm((prev) => ({ ...prev, company_id: e.target.value }))}
						className={`${selectClass} ${formErrors.company_id ? 'border-red-500' : ''}`}
					>
						<option value="">Select a customer...</option>
						{companyOptions.map((c) => (
							<option key={c.id} value={String(c.id)}>{c.name}</option>
						))}
					</select>
					{renderFieldError('company_id')}
					{modalMode === 'edit' && !isReassigning && (
						<p className="mt-4px text-12px text-brand-text-subtle">
							Selecting a different customer reassigns this contact to them.
						</p>
					)}
					{/* Reassignment callout — what the move means for existing records */}
					{isReassigning && (
						<Alert variant="warning" size="sm" className="mt-8px">
							This contact will move to {companyOptions.find((c) => String(c.id) === contactForm.company_id)?.name ?? 'the selected customer'} and stop
							appearing in the previous customer&apos;s contact pickers.
							{linkedTotal > 0 && (
								<>
									{' '}{editingContactCounts?.workOrders ?? 0} work order{(editingContactCounts?.workOrders ?? 0) === 1 ? '' : 's'} and{' '}
									{editingContactCounts?.inspections ?? 0} inspection{(editingContactCounts?.inspections ?? 0) === 1 ? '' : 's'} keep their
									historical link to this contact.
								</>
							)}
						</Alert>
					)}
				</div>

				{/* Title */}
				<div>
					<label htmlFor="modal-contact-title" className={labelClass}>Title</label>
					<input
						type="text"
						id="modal-contact-title"
						value={contactForm.title}
						onChange={(e) => setContactForm((prev) => ({ ...prev, title: e.target.value }))}
						className={inputClass}
						placeholder="e.g., Site Manager"
					/>
				</div>

				{/* Phone + Email on the same row */}
				<div className="grid grid-cols-1 tablet:grid-cols-2 gap-12px">
					<div>
						<label htmlFor="modal-contact-phone" className={labelClass}>Phone</label>
						<input
							type="tel"
							id="modal-contact-phone"
							value={contactForm.phone}
							onChange={(e) => setContactForm((prev) => ({ ...prev, phone: formatPhone(e.target.value) }))}
							className={inputClass}
							placeholder="(555) 123-4567"
						/>
					</div>
					<div>
						<label htmlFor="modal-contact-email" className={labelClass}>Email</label>
						<input
							type="email"
							id="modal-contact-email"
							value={contactForm.email}
							onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
							className={`${inputClass} ${formErrors.email ? 'border-red-500' : ''}`}
							placeholder="jane@customer.com"
						/>
						{renderFieldError('email')}
					</div>
				</div>

				{/* Notes */}
				<div>
					<label htmlFor="modal-contact-notes" className={labelClass}>Notes</label>
					<textarea
						id="modal-contact-notes"
						value={contactForm.notes}
						onChange={(e) => setContactForm((prev) => ({ ...prev, notes: e.target.value }))}
						className={inputClass}
						rows={3}
						placeholder="Additional notes about this contact..."
					/>
				</div>
			</div>
		);
	};

	/**
	 * Renders the Location modal form body.
	 * Includes fields for name, address, sqft, tax rate, business line,
	 * notes, and a multi-select checkbox list for company assignments.
	 */
	/** Merges a patch from the shared BuildingFieldset into locationForm, and
	 *	re-runs the duplicate check whenever the name or address moved — the two
	 *	fields that endpoint matches on. Create mode only: an edit is allowed to
	 *	keep matching itself. */
	const handleLocationFieldChange = useCallback((patch: Partial<LocationFormData>) => {
		setLocationForm((prev) => ({ ...prev, ...patch }));
		if (modalMode !== 'create') return;
		if (!('name' in patch) && !('address' in patch)) return;
		checkLocationDuplicate(patch.name ?? locationForm.name, patch.address ?? locationForm.address);
	}, [modalMode, locationForm.name, locationForm.address, checkLocationDuplicate]);

	/**
	 * Renders the Building (location) modal form body.
	 *
	 * Every field is the shared <BuildingFieldset> molecule, which the
	 * AddBuildingModal renders too — so the building form reached from Admin
	 * and the one reached from a job form are the same form. What is added
	 * here is Admin-only: the debounced duplicate warning, and the inline
	 * field errors from the modal's own validation.
	 */
	const renderLocationForm = () => (
		<BuildingFieldset
			idPrefix="modal-loc"
			values={locationForm}
			onChange={handleLocationFieldChange}
			businessLines={businessLines}
			companies={companyOptions}
			nameError={formErrors.name}
			companyError={formErrors.company_ids}
			/* Create only — editing a building shouldn't warn that it matches
			   itself. Also re-checked on blur, which catches a paste-and-tab
			   that never fired a keystroke. */
			duplicateWarning={modalMode === 'create' ? locationDuplicateWarning : null}
			onNameBlur={modalMode === 'create' ? () => checkLocationDuplicate(locationForm.name, locationForm.address) : undefined}
			customersHint="Customers are the companies you bill. Manage them (including billing address and terms) on the Companies tab."
			emptyCustomersText="No customers available. Create a company first."
			className="admin-page__location-fields"
			dataRef="page-admin-page-location-fields"
		/>
	);

	/* ============================================
	 * MAIN RENDER
	 * ============================================ */

	/* Compute labels based on active tab and modal mode */
	const activeTabLabel = TABS.find((t) => t.key === activeTab)?.label.toLowerCase() ?? activeTab;

	/* How many of the active tab's dropdowns are applied — null when the tab
	   has none, so the toolbar renders no Filters button for it. */
	const tabFilterCount: number | null = activeTab === 'users'
		? [roleFilter, statusFilter].filter(Boolean).length
		: activeTab === 'contacts'
			? [contactCompanyFilter, statusFilter].filter(Boolean).length
			: activeTab === 'locations'
				? (businessLineFilter ? 1 : 0)
				: null;

	/** Clears the active tab's dropdowns (the pop-up's Reset). Search is left alone. */
	const resetTabFilters = () => {
		setRoleFilter('');
		setStatusFilter('');
		setContactCompanyFilter('');
		setBusinessLineFilter('');
	};
	const addButtonLabel = activeTab === 'users' ? 'Add User' : activeTab === 'companies' ? 'Add Company' : activeTab === 'contacts' ? 'Add Contact' : 'Add Building';
	const modalTitle = modalMode === 'edit'
		? (activeTab === 'users' ? 'Edit User' : activeTab === 'companies' ? 'Edit Company' : activeTab === 'contacts' ? 'Edit Contact' : 'Edit Building')
		: (activeTab === 'users' ? 'Add New User' : activeTab === 'companies' ? 'Add New Company' : activeTab === 'contacts' ? 'Add New Contact' : 'Add New Building');
	const saveButtonLabel = modalSaving
		? (modalMode === 'edit' ? 'Saving...' : 'Creating...')
		: (modalMode === 'edit' ? 'Save Changes' : 'Create');

	/* Top-level access gate — render a Forbidden view for roles that may not
	 * manage users (matches the CMSPage convention). Backend authorization
	 * still enforces every mutation, but this stops a technician/reviewer who
	 * deep-links or bookmarks /admin from seeing the user-management dashboard
	 * and the Add User control. company_admin is allowed (manages its own
	 * company's users). */
	if (!hasAnyRole(['super_admin', 'admin', 'developer', 'company_admin'])) {
		return (
			<div className="admin-page__forbidden max-w-[640px] mx-auto mt-48px px-24px py-32px bg-white border border-brand-border rounded-md text-center">
				<h1 className="text-20px font-semibold text-brand-text mb-8px">Forbidden</h1>
				<p className="text-14px text-brand-text-muted">The Admin Dashboard is restricted to administrators.</p>
			</div>
		);
	}

	return (
		<PageContainer block="admin-page" dataRef="page-admin-page">
			{/* --- Page header (PageHeader molecule) --- */}
			<PageHeader
				title="Admin Dashboard"
				subtitle="Manage users, companies, and buildings."
			/>

			{/* --- Success message banner (auto-dismisses after 3s) --- */}
			{successMessage && (
				<Alert variant="success" className="mb-16px" onDismiss={() => setSuccessMessage(null)}>
					{successMessage}
				</Alert>
			)}

			{/* --- Tab navigation bar ---
				For non-super users (e.g., company_admin), hide the Companies tab since
				they can only manage users within their own company and should not see
				the full company management interface.

				wrap: six tabs at the 44px touch height overflow a phone, and a tab
				scrolled out of sight here hides an entire admin surface — so they
				wrap onto a second row rather than scrolling sideways. */}
			<TabBar
				className="admin-page__tabs"
				wrap
				activeKey={activeTab}
				onChange={(key) => handleTabChange(key as AdminTab)}
				tabs={TABS.filter((tab) => {
					/* Hide "Companies" tab for company_admin users who are not also super_admin/admin/developer.
					   These users are scoped to a single company and don't need company management. */
					if (tab.key === 'companies' && !isSuperUser) return false;
					/* Hide "Contacts" (the customer contact database) for the same
					   reason — cross-customer reassignment is a super-user action. */
					if (tab.key === 'contacts' && !isSuperUser) return false;
					/* Hide "Jobs" tab for non-super users (only admin, super_admin, developer can see it) */
					if (tab.key === 'jobs' && !isSuperUser) return false;
					/* Billing is super_admin ONLY — narrower than isSuperUser,
					   which also covers admin and developer. Raising an invoice
					   is an office accounting act; the backend refuses the
					   invoiced/closed transition and the bulk endpoint to
					   everyone else, so showing the tab would only offer a
					   surface whose every action 403s. */
					if (tab.key === 'billing' && !isSuperAdmin) return false;
					return true;
				})}
			/>

			{/* Billing brings its own stage tabs, filter strip and table — it
			    shares none of the search/Add chrome the entity tabs use, and
			    none of their queries either, so it replaces both outright
			    rather than rendering inside them. */}
			{activeTab === 'billing' ? <BillingTab /> : (
				<>
				{/* --- Search bar, filters, and Add button row --- */}
				<div className="admin-page__toolbar flex flex-col tablet:flex-row tablet:flex-wrap items-start tablet:items-center justify-between gap-12px mb-16px">
					{/* Left side: search + filters */}
					{/* min-w-0 lets the strip shrink at all (flex items default to
					    min-width:auto), and flex-wrap lets the filters stack onto a
					    second line rather than pushing the page sideways. Both are
					    needed on an iPad, where the sidebar leaves ~517px for a row
					    that wants 864. */}
					<div className="admin-page__filters flex flex-col tablet:flex-row tablet:flex-wrap items-start tablet:items-center gap-12px w-full min-w-0 tablet:w-auto tablet:flex-1">
						{/* Search — the shared molecule, which brings the clear
						    button and the 44px target with it. */}
						<SearchBar value={searchQuery} onChange={setSearchQuery} placeholder={`Search ${activeTabLabel}...`} className="admin-page__search w-full tablet:w-auto tablet:min-w-[240px]" />

						{/* Tab filters. Below desktop they fold into a Filters button +
						    pop-up so the list keeps the width; search stays out. Tabs
						    with no dropdowns (Companies, Jobs, Integrations) skip it. */}
						{tabFilterCount !== null && (
						<ResponsiveFilters title={`Filter ${activeTabLabel}`} activeCount={tabFilterCount} onReset={resetTabFilters} className="flex flex-col tablet:flex-row tablet:flex-wrap items-start tablet:items-center gap-12px" dataRef="page-admin-filters">
							{/* Role filter — only shown on the users tab */}
							{activeTab === 'users' && (
								<select
									value={roleFilter}
									onChange={(e) => setRoleFilter(e.target.value)}
									className={FILTER_SELECT_CLASS}
								>
									<option value="">All Roles</option>
									{ROLE_OPTIONS.map((r) => (
										<option key={r} value={r}>{formatRoleLabel(r)}</option>
									))}
								</select>
							)}

							{/* Status filter — only shown on the users tab */}
							{activeTab === 'users' && (
								<select
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
									className={FILTER_SELECT_CLASS}
								>
									<option value="">All Statuses</option>
									{STATUS_OPTIONS.map((s) => (
										<option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
									))}
								</select>
							)}

							{/* Customer + status filters — only shown on the contacts tab.
							    Narrow the contact database to one customer's people or to
							    inactive contacts pending reactivation. */}
							{activeTab === 'contacts' && (
								<>
									<select
										value={contactCompanyFilter}
										onChange={(e) => setContactCompanyFilter(e.target.value)}
										className={FILTER_SELECT_CLASS}
									>
										<option value="">All Customers</option>
										{companyOptions.map((c) => (
											<option key={c.id} value={String(c.id)}>{c.name}</option>
										))}
									</select>
									<select
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value)}
										className={FILTER_SELECT_CLASS}
									>
										<option value="">All Statuses</option>
										<option value="active">Active</option>
										<option value="inactive">Inactive</option>
									</select>
								</>
							)}

							{/* Business line filter — only shown on the locations tab.
							    Options come from the CMS-managed business_lines table. */}
							{activeTab === 'locations' && (
								<select
									value={businessLineFilter}
									onChange={(e) => setBusinessLineFilter(e.target.value)}
									className={FILTER_SELECT_CLASS}
								>
									<option value="">All Business Lines</option>
									{businessLines.map((bl) => (
										<option key={bl.id} value={bl.slug}>{bl.label}</option>
									))}
								</select>
							)}
						</ResponsiveFilters>
						)}
					</div>

					{/* Add button and Import CSV button container — hidden on Jobs tab
						(Jobs tab is read-only, no create/import actions needed) */}
					{activeTab !== 'jobs' && (
						<div className="admin-page__actions flex shrink-0 flex-wrap items-center gap-8px">
							{/* Import CSV button — only visible to admin/super_admin/developer roles.
								Opens the CsvImport modal for the currently active entity tab.
								Gated on IMPORTABLE_TABS so it stays off Integrations, which shares
								this toolbar but has no import endpoint behind it. */}
							{IMPORTABLE_TABS.includes(activeTab as ImportEntityType) && hasAnyRole(['admin', 'super_admin', 'developer']) && (
								<Button variant="secondary" size="sm" onClick={() => openCsvImport(activeTab as ImportEntityType)} className="whitespace-nowrap">
									Import CSV
								</Button>
							)}
							{/* Add button — opens the creation modal */}
							<Button variant="primary" size="sm" onClick={openCreateModal} className="whitespace-nowrap">
								{addButtonLabel}
							</Button>
						</div>
					)}
				</div>

				{/* --- Content area --- */}
				<div className="admin-page__content bg-white shadow rounded-lg p-24px">
					{/* Loading state */}
					{loading && (
						<div className="flex items-center justify-center py-32px">
							<p className="text-brand-text-subtle text-14px">Loading...</p>
						</div>
					)}

					{/* Error state */}
					{error && !loading && (
						<Alert variant="error" className="mb-16px">{error}</Alert>
					)}

					{/* Tab content — only rendered when not loading */}
					{!loading && !error && (
						<>
							{/* Company scope notice — shown on the Users tab for non-super users.
								Informs company_admin and similar roles that they are only seeing
								users within their own company, not the full platform user list. */}
							{activeTab === 'users' && !isSuperUser && (
								<Alert variant="info" size="sm" className="mb-12px">Showing users in your company</Alert>
							)}
							{activeTab === 'users' && renderUsersTab()}
							{activeTab === 'companies' && renderCompaniesTab()}
							{activeTab === 'contacts' && renderContactsTab()}
							{activeTab === 'locations' && renderLocationsTab()}
							{activeTab === 'jobs' && renderJobsTab()}

							{/* Integrations tab — HubSpot connection + sync UI */}
							{activeTab === 'integrations' && (
								<div>
									{/* HubSpot connection management card */}
									<HubSpotConnection
										status={hubspotStatus}
										loading={hubspotLoading}
										onStatusChange={fetchHubspotStatus}
									/>

									{/* HubSpot sync UI — only visible when connected */}
									{hubspotStatus?.connected && <HubSpotSync />}
								</div>
							)}

							{/* Empty state — shown when the data array for the active tab is empty
								(Jobs and Integrations tabs handle their own empty states) */}
							{activeTab !== 'jobs' && activeTab !== 'integrations' && ((activeTab === 'users' && users.length === 0) ||
							  (activeTab === 'companies' && companies.length === 0) ||
							  (activeTab === 'contacts' && contacts.length === 0) ||
							  (activeTab === 'locations' && locations.length === 0)) && (
								<p className="text-brand-text-subtle text-center py-32px text-14px">
									No {activeTabLabel} found. {searchQuery ? 'Try a different search term.' : `Add your first ${TAB_NOUN[activeTab]} to get started.`}
								</p>
							)}
						</>
					)}
				</div>
				</>
			)}

			{/* === CRUD MODAL (shared across all tabs) === */}
			<Modal
				isOpen={modalOpen}
				onClose={closeModal}
				title={modalTitle}
				size="md"
				/* Actions live in the pinned footer rather than at the end of the
				   form: these CRUD forms (the company one especially) are taller
				   than a laptop viewport, and buttons inside the scroll area were
				   getting clipped off the bottom of the panel. The submit button
				   reaches the form through the `form` attribute. */
				footer={(
					<div className="admin-page__modal-actions flex flex-col-reverse tablet:flex-row tablet:justify-end gap-8px">
						<Button type="button" variant="secondary" size="sm" onClick={closeModal} className="w-full tablet:w-auto">
							Cancel
						</Button>
						<Button type="submit" form={ADMIN_MODAL_FORM_ID} variant="primary" size="sm" disabled={modalSaving} className="w-full tablet:w-auto">
							{saveButtonLabel}
						</Button>
					</div>
				)}
			>
				{/* Modal error banner */}
				{modalError && (
					<Alert variant="error" className="mb-12px">{modalError}</Alert>
				)}

				{/* Modal form — dynamically renders fields based on active tab */}
				<form id={ADMIN_MODAL_FORM_ID} onSubmit={handleModalSubmit}>
					{activeTab === 'users' && renderUserForm()}
					{activeTab === 'companies' && renderCompanyForm()}
					{activeTab === 'contacts' && renderContactForm()}
					{activeTab === 'locations' && renderLocationForm()}
				</form>
			</Modal>

			{/* === CSV IMPORT MODAL === */}
			{/* Renders the CsvImport organism when the user clicks "Import CSV".
				The modal handles file selection, preview, upload, and results display.
				After import completes, handleCsvImportComplete refreshes the data list. */}
			<CsvImport
				isOpen={csvImportOpen}
				onClose={() => setCsvImportOpen(false)}
				entityType={csvImportEntity}
				onImportComplete={handleCsvImportComplete}
			/>

			{/* === RESET PASSWORD MODAL === */}
			{/* Uses the ConfirmModal organism with blurred backdrop.
				Contains a password input field with inline validation. */}
			<ConfirmModal
				isOpen={resetPwModalOpen}
				onClose={() => setResetPwModalOpen(false)}
				onConfirm={handleResetPasswordConfirm}
				title="Reset Password"
				message={resetPwTarget ? `Enter a temporary password for ${resetPwTarget.first_name} ${resetPwTarget.last_name}. The user will be required to change it on next login.` : ''}
				confirmLabel="Reset Password"
				confirmDisabled={resetPwValue.length < 12}
				loading={resetPwLoading}
			>
				<div className="space-y-8px">
					<label htmlFor="reset-pw-input" className="block text-14px font-medium text-brand-text">
						Temporary Password <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						id="reset-pw-input"
						value={resetPwValue}
						onChange={(e) => {
							setResetPwValue(e.target.value);
							/* Clear error as the user types */
							if (resetPwError) setResetPwError(null);
						}}
						className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
						placeholder="Minimum 12 characters"
						autoFocus
					/>
					{/* Character count hint */}
					<p className={`text-12px ${resetPwValue.length >= 12 ? 'text-green-600' : 'text-brand-text-subtle'}`}>
						{resetPwValue.length}/12 characters minimum
					</p>
					{/* Inline validation error */}
					{resetPwError && (
						<p className="text-12px text-red-600">{resetPwError}</p>
					)}
				</div>
			</ConfirmModal>

			{/* === DEACTIVATE CONFIRMATION MODAL === */}
			{/* Uses the ConfirmModal organism with blurred backdrop, replacing
				the old window.confirm() browser prompt. The record stays in the
				database and can be reactivated from the Inactive filter. */}
			<ConfirmModal
				isOpen={deactivateTarget !== null}
				onClose={() => setDeactivateTarget(null)}
				onConfirm={handleDeactivateConfirm}
				title={`Deactivate ${deactivateTarget ? TAB_NOUN[deactivateTarget.tab].charAt(0).toUpperCase() + TAB_NOUN[deactivateTarget.tab].slice(1) : 'Record'}`}
				message={`Are you sure you want to deactivate this ${deactivateTarget ? TAB_NOUN[deactivateTarget.tab] : 'record'}? It will be hidden from active listings and can be reactivated later.`}
				confirmLabel="Deactivate"
				variant="danger"
				loading={statusMutation.isPending}
			/>
		</PageContainer>
	);
};

export default AdminPage;
